import * as React from 'react';
import {
  DownloadSimpleIcon,
  XIcon,
  DotsSixIcon,
  InfoIcon,
  ListMagnifyingGlassIcon,
  MagnifyingGlassPlusIcon,
  CopyIcon,
  FileCloudIcon,
  PlusSquareIcon,
  PlusCircleIcon,
  MinusSquareIcon,
  MinusCircleIcon,
  ListPlusIcon,
  CaretLeftIcon,
  CaretRightIcon,
  BookmarksSimpleIcon,
  CheckIcon,
} from "@phosphor-icons/react";
import { Popover, PopoverTrigger, PopoverContent } from "@heroui/popover";
import { Popover as TpPopover } from '@tranquility-ai/theme-components/popover';
import '@tranquility-ai/theme-components/popover.css';
import { useTimePilotT, type TranslateFn } from '@/i18n/useTimePilotT';
import { MEDIA_PREVIEW_OVERLAY_SURFACE_CLASS } from '@/shared/inverse-overlay-surfaces';
import {
  handleDownload,
  getMediaType,
  getMediaRowDisplayType,
  isRestrictedFileType,
  extractPathAfterBulk,
  removeMD5Hash,
  safeDecodeURIComponent,
  storagePathForExtensionLookup,
  toProvenanceLookupPath,
  toSearchablePath,
  s3UriToObjectKey,
  resolvePreferredMediaDisplayForSource,
  resolveFullS3Url,
  resolveDisplayLookupUrl,
  resolveFileInfoMediaDisplayUrl,
  paintTranscriptMediaContext,
  resolveTranscriptUrlFromFileInfo,
  resolveTranscriptUrlFromSourceContext,
  isArchiveMemberSearchablePath,
  isContainerLikeSource,
  getFileInfoBatchFromServer,
  getFileInfoFromServer,
  getCaseDisplayName,
  isInlineJsonCitationDisplayable,
  isUnsetMediaTrimTime,
  mediaCaptionPath,
  normalizeMediaMembershipTime,
  mergeMediaCitationContextOntoSourceContext,
  shouldSyncMediaCitationOntoSource,
  formatTimeIndex,
} from '@/shared/functions';
import { CsamDetectionPill } from '../ui/media/csam-detection-pill';
import { Button } from "@tranquility-ai/theme-components/button";
import { TpTooltip } from "@tranquility-ai/theme-components/tooltip";
import { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react';
import { createPortal } from 'react-dom';
import { DismissibleBlurOverlay } from '../dismissible-blur-overlay';
import { EVIDENCE_LOCKER_PREVIEW_DIALOG_CLASS } from '../evidence-locker-preview-overlay';
import { MediaSource, MediaType } from '@/shared/types';
import { useDebounce } from 'use-debounce';
import { isDisplayableMediaType } from '@/shared/constants';
import {
  PATH_DISPLAY_WHITESPACE_CLASSNAME,
  PATH_FILENAME_VISIBLE_TRUNCATE_CLASSNAME,
} from '@/shared/uiConstants';
import { motion } from "framer-motion";
import MediaDisplay from '../ui/mediaDisplay';
import {
  AvailableSourcesFilterBar,
  DisplayedMediaStrip,
  DisplayMediaToggle,
  SourcesEmptyState,
  SourcesSectionHeader,
  StackedSourceListText,
  STACKED_SOURCES_SECTION_SHELL_CLASS,
  STACKED_SOURCES_SECTIONS_GAP_CLASS,
  STACKED_DISPLAYED_SOURCES_LIST_CLASS,
  formatStackedSourceDisplayPath,
} from '../event-sources';
import { ChatAttachmentFileIcon } from '../chat-attachment-icon';
import { EVENT_MODAL_SIDE_CONTAINER_WIDTH_PX } from '../ui/media/player';
import { resolveTranscriptPosition } from '../ui/media/transcriptPosition';
import { SourceCard } from '../sourceCard';
import { useToolsDrawer } from '../tools-drawer';
import deepEqual from 'deep-equal';
import path from 'path';
import { AuthContext, FullScreenContext, FeaturesContext } from '@/app/contexts';
import { useCsamAccess } from '@/lib/csam-access';
import { useJsonMediaDisplayEnabled } from '@/hooks/useMediaPlayerTransition';
import { useMediaSourceShare } from '../mediaSourceShare';
import { Spinner } from '@heroui/spinner';
import {
  dedupeMediaSources,
  formatProvenanceStatusLabel,
  getCurrentFileInfo,
  getProvenanceIconDisplaySize,
  getProvenanceToolbarIconClassNames,
} from '@/shared/clientFunctions'
import { ProvenanceIcon } from '@/shared/provenance-icon'
import { useCasesList } from '@/hooks/useCasesList';
import {
  caseFolderLabels,
  relabelCaseFolderSegments,
  resolveCaseTag,
} from '@/shared/case-summary';
import {
  captureSaveCallerStack,
  isCaptureSaveLogEnabled,
  logCaptureSave,
} from '@/shared/captureSaveLog';
import { useProvenanceFiles } from '@/hooks/useProvenanceFiles';
import { useProvenanceTreeSearch } from '@/hooks/useProvenanceTree';
import {
  findTreeNodeForSourceUrl,
  treeNodeToMediaSource,
  treeNodesToMediaSources,
} from '@/shared/provenance-tree-sources';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import { useEventMediaThumbnailBackfill } from '@/hooks/useEventMediaThumbnailBackfill';
import {
  fetchProvenanceTreePage,
  fetchProvenanceTreeSearch,
  type FileSummary,
} from '@/shared/cors-provenance-api';

const SOURCES_LIST_LIMIT = 100;
// Reserve part of the unfiltered picker for tree-only extracts. Flat `/files`
// results do not include archive members, so allowing them to consume every
// initial slot makes those members undiscoverable without a search.
const TREE_ONLY_SOURCES_LIST_RESERVE = 25;

// Add animation variants
const itemVariants = {
  dragging: {
    scale: 1.02,
    boxShadow: "0px 5px 10px rgba(0,0,0,0.2)",
    cursor: "grabbing",
    zIndex: 1,
    transition: {
      duration: 0.2
    }
  },
  normal: {
    scale: 1,
    boxShadow: "none",
    zIndex: 0,
    transition: {
      duration: 0.2
    }
  }
};

const buildMediaContextFromSource = (
  source: MediaSource,
  extras: Record<string, unknown> = {},
  options?: { stripPageMarkers?: boolean },
): Record<string, unknown> => {
  const merged: Record<string, unknown> = {
    ...(source.sourceContext || {}),
    ...extras,
  };
  // AV clips use start/end only — drop zip-parent page markers so membership
  // and media↔source mirroring stay page-agnostic for audio/video.
  const context = options?.stripPageMarkers
    ? Object.fromEntries(
        Object.entries(merged).filter(
          ([key]) => key !== 'page' && key !== 'pageNumber',
        ),
      )
    : merged;
  return paintTranscriptMediaContext(
    context,
    resolveTranscriptUrlFromSourceContext(source.sourceContext as Record<string, unknown>),
    resolveTranscriptUrlFromFileInfo(source.fileInfo),
  );
};

/** Same key as deduplication so enrichment can be looked up per list row (URL + page/time). */
const getSourceIdentityKey = (source: MediaSource): string => {
  const context = { ...(source?.sourceContext || {}), ...((source as any)?.mediaContext || {}) };
  const pageNumber =
    context?.pageNumber ?? context?.page ?? source.pageNumber;
  const startTime = context?.startTime;
  const endTime = context?.endTime;
  const mediaDisplayUrl =
    ((source as any).mediaUrl as string | undefined)?.trim() ||
    ((source as any).mediaSrc as string | undefined)?.trim() ||
    '';
  const url = source.sourceUrl?.trim() || mediaDisplayUrl || '';
  const mediaType = getMediaType(source, source.fileInfo);
  const isMediaFile = mediaType === 'audio' || mediaType === 'video';
  const hasPageNumber = pageNumber !== undefined && pageNumber !== null && pageNumber > 0;
  const previewObjectUrl = source.fileInfo?.previewUrl || '';
  const previewFilePath = source.previewFilePath || '';
  const hasPreviewFilePath = previewFilePath !== undefined && previewFilePath !== null && previewFilePath !== '';
  const hasPreviewObjectUrl = previewObjectUrl !== undefined && previewObjectUrl !== null && previewObjectUrl !== '';
  const previewUrl = hasPreviewObjectUrl ? previewObjectUrl : hasPreviewFilePath ? previewFilePath : '';
  const contentKey = (source.content || '').trim().slice(0, 96);
  const contentLooksLikeJson = (() => {
    if (!contentKey) return false;
    const full = (source.content || '').trim();
    if (
      !(
        (full.startsWith('{') && full.endsWith('}')) ||
        (full.startsWith('[') && full.endsWith(']'))
      )
    ) {
      return false;
    }
    try {
      JSON.parse(full);
      return true;
    } catch {
      return false;
    }
  })();

  // JSON citation rows (content-only or with a proxyable mediaUrl): include
  // excerpt so distinct citations that share a canonical sourceUrl are not
  // collapsed, and so saved event sources (no mediaUrl after reload) still
  // match chat picker rows that still carry a processed mediaUrl.
  // Require stored/path json *or* parseable JSON ``content`` — event source
  // rows often keep an inferred parent type (mbox/zip/…) while still carrying
  // distinct inline JSON in ``content``.
  if (
    contentKey &&
    (source.mediaType === 'json' || mediaType === 'json' || contentLooksLikeJson)
  ) {
    const parts: string[] = [url, `content:${contentKey}`];
    if (hasPageNumber) parts.push(String(pageNumber));
    return parts.filter(Boolean).join('-');
  }

  const parts: string[] = [url];
  if (previewUrl !== '') parts.push(previewUrl);

  if (isMediaFile) {
    // Align with membership: omit unset / full-file zeros, and normalize
    // equivalent trims (``90`` / ``"90"`` / ``"1:30"``) to one key so
    // deduplicateSources does not keep sibling full-file or same-clip rows.
    const normalizedStart = normalizeMediaMembershipTime(startTime);
    const normalizedEnd = normalizeMediaMembershipTime(endTime);
    if (normalizedStart !== null) parts.push(String(normalizedStart));
    if (normalizedEnd !== null) parts.push(String(normalizedEnd));
    return parts.join('-');
  }
  if (hasPageNumber) {
    parts.push(String(pageNumber));
    return parts.join('-');
  }
  if (previewUrl !== '') {
    return parts.join('-');
  }
  return url;
};

const deduplicateSources = (sources: MediaSource[]): MediaSource[] => {
  const seen = new Map<string, MediaSource>();
  if (!sources || sources.length === 0) return [];
  sources?.forEach(source => {
    const key = getSourceIdentityKey(source);
    if (!seen.has(key)) {
      seen.set(key, source);
    }
  });
  return Array.from(seen.values());
};

/**
 * The add-source list combines chat/flat rows with tree-search rows. A chat
 * citation can win its position in the list while carrying only the virtual
 * ``…/archive.zip/member`` searchable path. Reattach the richer tree row
 * before previewing so the processed extract URL is never lost at that merge.
 */
const mergeTreeSourceForPreview = (
  source: MediaSource,
  nodes: Parameters<typeof treeNodesToMediaSources>[0] | undefined,
): MediaSource => {
  if (!nodes?.length) return source;

  const sourcePath = normalizeProvenanceStatusPath(
    source.sourceUrl || source.sourceFilePath || '',
  );
  if (!sourcePath) return source;

  const treeSource = treeNodesToMediaSources(nodes).find((candidate) =>
    getSourceProvenanceStatusPathCandidates(candidate).some(
      (candidatePath) => normalizeProvenanceStatusPath(candidatePath) === sourcePath,
    ),
  );
  if (!treeSource) return source;

  return {
    ...source,
    ...treeSource,
    // Keep the selected row's identity / annotation, but use tree metadata and
    // its processed URL for preview.
    sourceUrl: source.sourceUrl || treeSource.sourceUrl,
    sourceFilePath: source.sourceFilePath || treeSource.sourceFilePath,
    sourceContext: source.sourceContext,
  };
};

const normalizeProvenanceStatusPath = (value: string | undefined | null): string => {
  const raw = (value ?? '').trim().replace(/@/g, '');
  if (!raw) return '';
  const withoutQuery = raw.split(/[?#]/)[0];
  const decoded = (() => {
    try {
      return decodeURIComponent(withoutQuery);
    } catch {
      return withoutQuery;
    }
  })();
  const dataBulkIndex = decoded.toLowerCase().indexOf('data/bulk/');
  if (dataBulkIndex >= 0) {
    return decoded.slice(dataBulkIndex).replace(/^\/+/, '').toLowerCase();
  }
  return toProvenanceLookupPath(decoded).replace(/^\/+/, '').toLowerCase();
};

const getSourceProvenanceStatusPathCandidates = (source: MediaSource): string[] => {
  const candidates = [
    source.sourceUrl,
    source.sourceFilePath,
    source.s3Url,
    source.originalUrl,
    source.fileInfo?.fileUrl,
    source.fileInfo?.searchablePath,
    source.dispatchedFilePath,
    source.previewFilePath,
    source.mediaUrl,
    source.mediaSrc,
  ];
  return [...new Set(candidates.map(normalizeProvenanceStatusPath).filter(Boolean))];
};

/** Whether provenance metadata describes this exact source rather than a parent container. */
const fileInfoMatchesSource = (
  source: MediaSource,
  fileInfo: FileSummary | null | undefined,
): boolean => {
  if (!fileInfo) return false;
  const sourcePath = normalizeProvenanceStatusPath(
    source.sourceUrl || source.sourceFilePath || '',
  );
  if (!sourcePath) return false;
  return [fileInfo.searchablePath, fileInfo.fileUrl]
    .map(normalizeProvenanceStatusPath)
    .some((path) => path === sourcePath);
};

/** Merge source/media context so list annotations can read trim times from either. */
const getUnifiedSourceAnnotationContext = (
  sourceObject: MediaSource,
): Record<string, unknown> => ({
  ...(sourceObject.sourceContext || {}),
  ...((sourceObject as MediaSource).mediaContext || {}),
});

/** Trim bounds from merged source/media context for +/- and mediaContext writes. */
const getUnifiedSourceTrimExtras = (
  sourceObject: MediaSource,
): Record<string, unknown> => {
  const context = getUnifiedSourceAnnotationContext(sourceObject);
  const extras: Record<string, unknown> = {};
  if (!isUnsetMediaTrimTime(context.startTime)) {
    extras.startTime = context.startTime;
  }
  if (!isUnsetMediaTrimTime(context.endTime)) {
    extras.endTime = context.endTime;
  }
  return extras;
};

/** Page ref from merged source/media context (matches toggleMediaSource / list annotation). */
const resolveUnifiedSourcePageRef = (
  source: MediaSource,
): number | string | undefined => {
  const context = getUnifiedSourceAnnotationContext(source);
  const pageRef =
    context.pageNumber ?? context.page ?? source.pageNumber;
  if (pageRef === undefined || pageRef === null || pageRef === '') {
    return undefined;
  }
  return pageRef as number | string;
};

/**
 * Audio/video: start/end only. Containers (bare zip/ufdr/mbox): no page label.
 * Other document types: page only (never time indices).
 */
function formatUnifiedSourceListAnnotation(sourceObject: MediaSource, t: TranslateFn): string {
  const mt = getMediaType(sourceObject, sourceObject.fileInfo);
  const context = getUnifiedSourceAnnotationContext(sourceObject);
  if (mt === 'audio' || mt === 'video') {
    const parts: string[] = [];
    if (!isUnsetMediaTrimTime(context.startTime)) {
      parts.push(t('sources.startParen', { time: String(context.startTime) }));
    }
    if (!isUnsetMediaTrimTime(context.endTime)) {
      parts.push(t('sources.endParen', { time: String(context.endTime) }));
    }
    return parts.join(' ');
  }
  // A bare archive/container citation is not a paged document — "(Page 1)" is noise.
  if (isContainerLikeSource(sourceObject, sourceObject.fileInfo)) {
    return '';
  }
  const pageRef =
    context.pageNumber ?? context.page ?? sourceObject.pageNumber;
  return pageRef !== undefined && pageRef !== null && pageRef !== ''
    ? t('sources.pageParen', { page: String(pageRef) })
    : '';
}

/** Stacked modal rows: metadata inline with the filename per Figma (no colons on AV trims). */
function formatStackedSourceListAnnotation(sourceObject: MediaSource, t: TranslateFn): string {
  const mt = getMediaType(sourceObject, sourceObject.fileInfo);
  const context = getUnifiedSourceAnnotationContext(sourceObject);
  if (mt === 'audio' || mt === 'video') {
    const parts: string[] = [];
    if (!isUnsetMediaTrimTime(context.startTime)) {
      parts.push(t('sources.startBare', { time: formatTimeIndex(String(context.startTime)) }));
    }
    if (!isUnsetMediaTrimTime(context.endTime)) {
      parts.push(t('sources.endBare', { time: formatTimeIndex(String(context.endTime)) }));
    }
    return parts.join(' ');
  }
  if (isContainerLikeSource(sourceObject, sourceObject.fileInfo)) {
    return '';
  }
  const pageRef =
    context.pageNumber ?? context.page ?? sourceObject.pageNumber;
  return pageRef !== undefined && pageRef !== null && pageRef !== ''
    ? t('sources.pageParen', { page: String(pageRef) })
    : '';
}

function stackedSourceFilename(
  sourceObject: MediaSource,
  options: UnifiedSourceListPathOptions | undefined,
  t: TranslateFn,
): string {
  const pathLabel = formatUnifiedSourceListPath(sourceObject, options, t);
  const segments = pathLabel.split('/').filter(Boolean);
  return segments.pop() || t('sources.unknown');
}

/** Everything after the first ``/bulk/`` (or a leading ``bulk/``) in a storage path. */
function pathAfterFirstBulk(url: string): string {
  const lower = url.toLowerCase();
  const slashBulk = lower.indexOf('/bulk/');
  const afterBulkIndex =
    slashBulk >= 0
      ? slashBulk + '/bulk/'.length
      : lower.startsWith('bulk/')
        ? 'bulk/'.length
        : -1;
  if (afterBulkIndex < 0) return '';
  // Keep ``#`` in keys (``report#1.pdf``); only drop ``?`` and ``#page=``.
  const rest = storagePathForExtensionLookup(url.slice(afterBulkIndex));
  return removeMD5Hash(safeDecodeURIComponent(rest));
}

export type UnifiedSourceListPathOptions = {
  /**
   * All Cases: show the path from after ``/bulk/`` so the case id stays in the
   * label. A single case still uses parent-dir/filename.
   */
  includePathAfterBulk?: boolean;
  /**
   * ``folder_token`` → case display name, from ``useCaseFolderLabels``. Without
   * it a token-bearing case renders a raw UUID as its folder here — most visibly
   * under All Cases, where the case folder is part of every label. Display only:
   * the underlying ``sourceUrl`` is never rewritten.
   */
  caseFolderLabels?: Map<string, string> | null;
};

/** ``archive.zip/inner.mp4`` (or parent/filename) for Selected Sources labels. */
export function formatUnifiedSourceListPath(
  sourceObject: MediaSource,
  options?: UnifiedSourceListPathOptions,
  t?: TranslateFn,
): string {
  const url = (
    sourceObject.sourceUrl ||
    sourceObject.sourceFilePath ||
    sourceObject.truncatedFilePath ||
    ''
  ).replace(/@/g, '');
  const label = (path: string) => relabelCaseFolderSegments(path, options?.caseFolderLabels);
  if (options?.includePathAfterBulk) {
    const afterBulk = pathAfterFirstBulk(url);
    if (afterBulk) return label(afterBulk);
  }
  const parts = url.split('/').filter(Boolean);
  const filename = parts.pop() || (t ? t('sources.unknown') : 'Unknown');
  const parentDir = parts.pop() || '';
  return parentDir ? label(`${parentDir}/${filename}`) : filename;
}

function formatUnifiedSourceListLabel(
  sourceObject: MediaSource,
  options: UnifiedSourceListPathOptions | undefined,
  t: TranslateFn,
): string {
  const pathLabel = formatUnifiedSourceListPath(sourceObject, options, t);
  const ann = formatUnifiedSourceListAnnotation(sourceObject, t);
  return ann ? `${pathLabel} ${ann}` : pathLabel;
}

/** Hover tooltip for truncated source-list paths on dark event-modal chrome. */
function UnifiedSourcePathTooltip({
  pathLabel,
  children,
  disabled = false,
}: {
  pathLabel: string;
  children: React.ReactElement;
  disabled?: boolean;
}) {
  return (
    <TpTooltip
      content={
        <span className={`break-all ${PATH_DISPLAY_WHITESPACE_CLASSNAME}`}>
          {pathLabel}
        </span>
      }
      variant="inverted"
      placement="top"
      delayDuration={400}
      sideOffset={6}
      disabled={disabled || !pathLabel}
    >
      {children}
    </TpTooltip>
  );
}

/**
 * Native path trigger — theme Button wraps children in an ``inline-flex`` span
 * that prevents start-ellipsis (filename stays clipped on the right).
 */
const UNIFIED_SOURCE_PATH_TRIGGER_CLASSNAME =
  'box-border block w-full min-w-0 max-w-full overflow-hidden bg-transparent border-none p-0 m-0 text-right cursor-pointer appearance-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white';

/** Evidence Locker-style path: ellipsis at the start, filename always visible. */
function UnifiedSourceListPathText({
  pathLabel,
  modifier,
  sourceTypeAttr,
}: {
  pathLabel: string;
  modifier?: string;
  sourceTypeAttr?: string;
}) {
  return (
    <div
      {...(sourceTypeAttr ? { 'data-source-type': sourceTypeAttr } : {})}
      className="min-w-0 w-full max-w-full text-right"
    >
      <div
        dir="rtl"
        className={`block w-full max-w-full ${PATH_FILENAME_VISIBLE_TRUNCATE_CLASSNAME} text-right text-white/100 text-sm`}
      >
        &lrm;{pathLabel}
      </div>
      {modifier ? (
        <div
          dir="rtl"
          className={`block w-full max-w-full ${PATH_FILENAME_VISIBLE_TRUNCATE_CLASSNAME} text-right text-white/65 text-xs`}
        >
          &lrm;{modifier}
        </div>
      ) : null}
    </div>
  );
}

const getValidSources = (sources: MediaSource[]): MediaSource[] => {
  return sources?.filter((source) => {
    const filename = (source.truncatedFilePath || source.sourceUrl || '').split('/').pop() || 'Unknown';
    return source.sourceUrl &&
           source.sourceUrl.trim() !== "" &&
           source.sourceUrl !== "Unknown" &&
           filename !== "Unknown";
  });
}

/**
 * Resolve a proxyable display URL for a source row: preferred path first, then
 * citation-attached mediaUrl/mediaSrc (processCitation may set those while
 * preferred still points at a virtual archive-member path), then content-only JSON.
 */
const resolveSourceDisplayableMedia = (
  source: MediaSource,
  fileInfo: FileSummary | null | undefined,
  jsonMediaDisplayEnabled: boolean,
) => {
  const preferredDisplay = resolvePreferredMediaDisplayForSource(source, fileInfo);
  const existingUrl = (source.mediaUrl || source.mediaSrc || '').trim();
  const existingType = existingUrl
    ? getMediaType(
        {
          mediaType: source.mediaType || 'unknown',
          mediaUrl: existingUrl,
        } as MediaSource,
        fileInfo,
      )
    : ('unknown' as MediaType);
  const existingDisplayable =
    !!existingUrl &&
    !isArchiveMemberSearchablePath(existingUrl) &&
    isDisplayableMediaType(
      existingType !== 'unknown' ? existingType : source.mediaType,
      { jsonMediaDisplay: jsonMediaDisplayEnabled },
    );
  const preferredDisplayable =
    !!preferredDisplay.mediaUrl &&
    !isArchiveMemberSearchablePath(preferredDisplay.mediaUrl) &&
    isDisplayableMediaType(preferredDisplay.mediaType, {
      jsonMediaDisplay: jsonMediaDisplayEnabled,
    });
  const hasDisplayableUrl = preferredDisplayable || existingDisplayable;
  const contentOnlyJson =
    !hasDisplayableUrl &&
    isInlineJsonCitationDisplayable(
      source,
      preferredDisplay.mediaType,
      jsonMediaDisplayEnabled,
    );
  const displayUrl = preferredDisplayable
    ? preferredDisplay.mediaUrl!
    : existingDisplayable
      ? existingUrl
      : '';
  const mediaType = preferredDisplayable
    ? preferredDisplay.mediaType
    : existingDisplayable
      ? ((existingType !== 'unknown' ? existingType : source.mediaType) ||
        preferredDisplay.mediaType)
      : contentOnlyJson
        ? 'json'
        : preferredDisplay.mediaType;

  return {
    preferredDisplay,
    preferredDisplayable,
    existingDisplayable,
    hasDisplayableUrl,
    contentOnlyJson,
    displayUrl,
    mediaType,
  };
};

const isMediaSource = (
  source: MediaSource,
  fileInfo: FileSummary | null | undefined,
  jsonMediaDisplayEnabled = false,
): boolean => {
  const resolved = resolveSourceDisplayableMedia(source, fileInfo, jsonMediaDisplayEnabled);
  return resolved.hasDisplayableUrl || resolved.contentOnlyJson;
};

const getPreferredSourceDisplay = (source: MediaSource) =>
  resolvePreferredMediaDisplayForSource(source, source.fileInfo);

const getPreferredSourceMediaUrl = (source: MediaSource): string =>
  getPreferredSourceDisplay(source).mediaUrl || '';

/** First non-empty URL that is not a virtual ``…/archive.zip/member`` path. */
const firstPlayableUrl = (
  ...urls: Array<string | undefined | null>
): string => {
  for (const url of urls) {
    if (typeof url === 'string' && url.trim() && !isArchiveMemberSearchablePath(url)) {
      return url.trim();
    }
  }
  return '';
};

type SourcePlayableEnrichment = {
  fileInfo: FileSummary | null;
  sourceType: string | undefined;
  mediaUrl?: string;
  mediaSrc?: string;
  s3Url?: string;
};

/**
 * When a Selected Sources row only keeps a virtual archive-member path after
 * reload, the playable object often still lives on the matching media row.
 * Copy that URL (and type/fileInfo) so ``isMediaSource`` / the +/- control work.
 */
const findMatchingMediaPlayable = (
  source: MediaSource,
  mediaList: MediaSource[] | undefined,
): {
  playableUrl: string;
  fileInfo?: FileSummary | null;
  mediaType?: string;
} | null => {
  if (!mediaList?.length) return null;
  const preferred = getPreferredSourceMediaUrl(source);
  for (const media of mediaList) {
    if (!mediaMatchesSourceIdentity(media, source, preferred, mediaList)) {
      continue;
    }
    const mediaPreviewUrl =
      typeof media.mediaContext?.previewUrl === 'string'
        ? media.mediaContext.previewUrl
        : undefined;
    const playableUrl = firstPlayableUrl(
      media.mediaUrl,
      media.mediaSrc,
      media.s3Url,
      media.previewFilePath,
      media.dispatchedFilePath,
      mediaPreviewUrl,
    );
    if (!playableUrl) continue;
    return {
      playableUrl,
      fileInfo: media.fileInfo,
      mediaType:
        media.mediaType && media.mediaType !== 'unknown'
          ? media.mediaType
          : getMediaType(media, media.fileInfo),
    };
  }
  return null;
};

/** Stamp a playable URL onto a source row without changing list identity. */
const withPlayableSourceUrls = (
  source: MediaSource,
  playableUrl: string,
  extras?: {
    fileInfo?: FileSummary | null;
    sourceType?: string;
  },
): MediaSource => {
  const fileInfo =
    extras?.fileInfo !== undefined ? extras.fileInfo : source.fileInfo;
  const sourceType =
    extras?.sourceType ||
    source.sourceType ||
    getMediaType(
      { ...source, mediaUrl: playableUrl, mediaSrc: playableUrl } as MediaSource,
      fileInfo,
    );
  const nextMediaType =
    source.mediaType && source.mediaType !== 'unknown'
      ? source.mediaType
      : sourceType;
  const nextS3Url =
    source.s3Url && !isArchiveMemberSearchablePath(source.s3Url)
      ? source.s3Url
      : playableUrl;
  // Preserve referential identity when trim/media updates re-seed the same URL.
  if (
    source.mediaUrl === playableUrl &&
    source.mediaSrc === playableUrl &&
    source.s3Url === nextS3Url &&
    source.fileInfo === fileInfo &&
    source.sourceType === sourceType &&
    source.mediaType === nextMediaType
  ) {
    return source;
  }
  return {
    ...source,
    ...(fileInfo ? { fileInfo } : {}),
    ...(sourceType
      ? {
          sourceType,
          mediaType: nextMediaType,
        }
      : {}),
    mediaUrl: playableUrl,
    mediaSrc: playableUrl,
    s3Url: nextS3Url,
  };
};

/**
 * Seed zip/UFDR member rows with a playable URL from tree-search cache or the
 * event media array before async provenance lookups finish.
 */
const seedSourceWithPlayableUrl = (
  source: MediaSource,
  treeNodes: Parameters<typeof treeNodesToMediaSources>[0] | undefined,
  mediaList: MediaSource[] | undefined,
): MediaSource => {
  const fromTree = mergeTreeSourceForPreview(source, treeNodes);
  const alreadyPlayable = firstPlayableUrl(
    fromTree.mediaUrl,
    fromTree.mediaSrc,
    fromTree.s3Url,
    resolveFileInfoMediaDisplayUrl(fromTree.fileInfo, ''),
  );
  if (alreadyPlayable) {
    return withPlayableSourceUrls(fromTree, alreadyPlayable, {
      fileInfo: fromTree.fileInfo,
      sourceType: fromTree.sourceType || getMediaType(fromTree, fromTree.fileInfo),
    });
  }
  const borrowed = findMatchingMediaPlayable(fromTree, mediaList);
  if (!borrowed) return fromTree;
  return withPlayableSourceUrls(fromTree, borrowed.playableUrl, {
    fileInfo: borrowed.fileInfo ?? fromTree.fileInfo,
    sourceType: borrowed.mediaType,
  });
};

/**
 * Re-hydrate from parent ``value`` without dropping local-only enrichment
 * (playable URLs / fileInfo from async provenance or media borrow).
 */
const preserveLocalSourceEnrichment = (
  source: MediaSource,
  prevRow: MediaSource | undefined,
  mediaList: MediaSource[] | undefined,
): MediaSource => {
  const seeded = seedSourceWithPlayableUrl(source, undefined, mediaList);
  if (!prevRow) return seeded;
  const parentPlayable = firstPlayableUrl(
    source.mediaUrl,
    source.mediaSrc,
    source.s3Url,
    resolveFileInfoMediaDisplayUrl(source.fileInfo, ''),
  );
  if (parentPlayable) return seeded;
  const prevPlayable = firstPlayableUrl(
    prevRow.mediaUrl,
    prevRow.mediaSrc,
    prevRow.s3Url,
    resolveFileInfoMediaDisplayUrl(prevRow.fileInfo, ''),
  );
  if (!prevPlayable && !prevRow.fileInfo) return seeded;
  return {
    ...seeded,
    ...(prevRow.fileInfo || seeded.fileInfo
      ? { fileInfo: prevRow.fileInfo ?? seeded.fileInfo }
      : {}),
    ...(prevPlayable || seeded.mediaUrl || seeded.mediaSrc
      ? {
          mediaUrl: prevRow.mediaUrl || seeded.mediaUrl,
          mediaSrc: prevRow.mediaSrc || seeded.mediaSrc,
          s3Url:
            prevRow.s3Url && !isArchiveMemberSearchablePath(prevRow.s3Url)
              ? prevRow.s3Url
              : seeded.s3Url && !isArchiveMemberSearchablePath(seeded.s3Url)
                ? seeded.s3Url
                : prevPlayable || seeded.mediaUrl || seeded.mediaSrc,
        }
      : {}),
    sourceType: prevRow.sourceType ?? seeded.sourceType,
    ...(prevRow.mediaType && prevRow.mediaType !== 'unknown'
      ? { mediaType: prevRow.mediaType }
      : seeded.mediaType
        ? { mediaType: seeded.mediaType }
        : {}),
  };
};

/** Ensure Selected Media thumbnails/player get a typed playable URL. */
const resolveMediaForDisplay = (media: MediaSource): MediaSource => {
  const mediaPreviewUrl =
    typeof media.mediaContext?.previewUrl === 'string'
      ? media.mediaContext.previewUrl
      : undefined;
  const playable = firstPlayableUrl(
    media.mediaUrl,
    media.mediaSrc,
    media.s3Url,
    media.previewFilePath,
    media.dispatchedFilePath,
    mediaPreviewUrl,
  );
  const mediaType =
    media.mediaType && media.mediaType !== 'unknown'
      ? media.mediaType
      : getMediaType(
          {
            ...media,
            ...(playable ? { mediaUrl: playable, mediaSrc: playable } : {}),
          } as MediaSource,
          media.fileInfo,
        );
  // Preserve referential identity when nothing changes so memoized MediaDisplay
  // (and trim-drag layout freeze) are not defeated by a new object each render.
  const urlUnchanged =
    !playable ||
    (media.mediaUrl === playable && media.mediaSrc === playable);
  const typeUnchanged = !mediaType || media.mediaType === mediaType;
  if (urlUnchanged && typeUnchanged) {
    return media;
  }
  return {
    ...media,
    ...(playable
      ? { mediaUrl: playable, mediaSrc: playable }
      : {}),
    ...(mediaType ? { mediaType } : {}),
  };
};

/**
 * Build a Selected Sources row from a media item so every Selected Media entry
 * also appears in the sources list with zip-member path + AV modifiers.
 */
const mediaItemToSelectedSource = (media: MediaSource): MediaSource => {
  const hasMediaUrl = !!(media.mediaUrl || media.mediaSrc);
  const mediaPreviewUrl =
    typeof media.mediaContext?.previewUrl === 'string'
      ? media.mediaContext.previewUrl
      : undefined;
  const playable = firstPlayableUrl(
    media.mediaUrl,
    media.mediaSrc,
    media.s3Url,
    media.previewFilePath,
    media.dispatchedFilePath,
    mediaPreviewUrl,
  );
  const searchablePath =
    typeof media.fileInfo?.searchablePath === 'string'
      ? media.fileInfo.searchablePath.trim()
      : '';
  const sourceUrl = (media.sourceUrl || '').trim();
  const sourceFilePath = (media.sourceFilePath || '').trim();
  // Prefer a virtual archive-member path so the list shows
  // ``archive.zip/inner.mp4`` rather than the bare container or a hash key.
  const archiveMemberPath = [searchablePath, sourceUrl, sourceFilePath].find(
    (candidate) => !!candidate && isArchiveMemberSearchablePath(candidate),
  );
  const identityUrl =
    archiveMemberPath ||
    sourceUrl ||
    sourceFilePath ||
    searchablePath ||
    media.originalUrl ||
    playable ||
    media.mediaUrl ||
    media.mediaSrc ||
    '';
  const sourceType = media.mediaType || getMediaType(media, media.fileInfo);
  // Drop page markers copied from a parent zip citation; AV rows use start/end.
  const mergedContext = {
    ...(media.sourceContext || {}),
    ...(media.mediaContext || {}),
  };
  const sourceContext =
    sourceType === 'audio' || sourceType === 'video'
      ? Object.fromEntries(
          Object.entries(mergedContext).filter(
            ([key]) => key !== 'page' && key !== 'pageNumber',
          ),
        )
      : mergedContext;
  return {
    sourceUrl: identityUrl,
    sourceFilePath: sourceFilePath || identityUrl,
    sourceType,
    mediaType: sourceType,
    ...(hasMediaUrl || playable
      ? {
          mediaUrl: playable || media.mediaUrl || media.mediaSrc,
          mediaSrc: playable || media.mediaSrc || media.mediaUrl,
        }
      : {}),
    content: media.content,
    sourceContext,
    fileInfo: media.fileInfo,
    ...(media.previewFilePath ? { previewFilePath: media.previewFilePath } : {}),
    ...(media.dispatchedFilePath
      ? { dispatchedFilePath: media.dispatchedFilePath }
      : {}),
    ...(media.s3Url && !isArchiveMemberSearchablePath(media.s3Url)
      ? { s3Url: media.s3Url }
      : playable
        ? { s3Url: playable }
        : {}),
  };
};

/** Identity key for content-only JSON media rows (matches answer-item buildEventMedia). */
const citationContentMatchKey = (source: { content?: string } | null | undefined): string =>
  (source?.content || '').trim().slice(0, 96);

/** Canonical citation path for content-only rows (sourceUrl preferred over sourceFilePath). */
const citationCanonicalSourcePath = (source: {
  sourceUrl?: string;
  sourceFilePath?: string;
} | null | undefined): string =>
  (source?.sourceUrl || source?.sourceFilePath || '').trim();

/**
 * Legacy reload fallback: source lost ``content`` while media kept it. Only
 * match when this media is the unique JSON candidate for the shared path/URL
 * among ``peerMedia`` — shared-path siblings stay unmatched.
 */
const uniqueLegacyJsonMediaMatch = (
  media: MediaSource,
  source: MediaSource,
  preferredSourceUrl: string,
  peerMedia: MediaSource[],
): boolean => {
  if (media.mediaType !== 'json' || !citationContentMatchKey(media)) {
    return false;
  }
  if (citationContentMatchKey(source)) {
    return false;
  }
  const sourcePath =
    citationCanonicalSourcePath(source) || (preferredSourceUrl || '').trim();
  const mediaPath = citationCanonicalSourcePath(media);
  if (sourcePath && mediaPath && sourcePath === mediaPath) {
    const peers = peerMedia.filter(
      (m) =>
        m.mediaType === 'json' &&
        citationCanonicalSourcePath(m) === sourcePath &&
        !!citationContentMatchKey(m),
    );
    return peers.length === 1;
  }
  const mediaUrls = [media.mediaSrc, media.mediaUrl].filter(
    (url): url is string => typeof url === 'string' && url.length > 0,
  );
  const sourceUrls = [
    source.sourceUrl,
    source.sourceFilePath,
    preferredSourceUrl,
    source.mediaUrl,
    source.mediaSrc,
  ].filter((url): url is string => typeof url === 'string' && url.length > 0);
  const sharedUrl = mediaUrls.find((url) => sourceUrls.includes(url));
  if (sharedUrl) {
    const peers = peerMedia.filter(
      (m) =>
        m.mediaType === 'json' &&
        (m.mediaUrl === sharedUrl || m.mediaSrc === sharedUrl) &&
        !!citationContentMatchKey(m),
    );
    return peers.length === 1;
  }
  // Content-only media with no canonical path cannot be safely linked to a
  // source that also lacks content — a lone event-wide match could attach the
  // wrong excerpt after reload.
  return false;
};

/** Collect non-empty URL strings for source↔media identity checks. */
const collectIdentityUrls = (
  ...urls: Array<string | undefined | null>
): string[] =>
  urls.filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
    .map((url) => url.trim());

/**
 * Normalize a URL/path for source↔media identity.
 *
 * Zip extracts often store the playable object as ``s3://bucket/files/…`` on one
 * side and ``files/…`` on the other; virtual member paths may be
 * ``data/bulk/…/archive.zip/inner`` vs ``…/archive.zip/inner``. Comparing raw
 * strings left membership green-state / toggle remove broken for those rows.
 */
const normalizeIdentityUrl = (url: string): string => {
  let p = url.trim();
  if (!p) return '';
  // Drop query strings from presigned / proxy URLs; keep ``#`` in object keys.
  const queryIdx = p.indexOf('?');
  if (queryIdx >= 0) p = p.slice(0, queryIdx);
  if (p.startsWith('/api/proxy/')) {
    try {
      p = decodeURIComponent(p.slice('/api/proxy/'.length));
    } catch {
      p = p.slice('/api/proxy/'.length);
    }
  }
  const s3Key = s3UriToObjectKey(p);
  if (s3Key) p = s3Key;
  p = p.replace(/^\/+/, '');
  // Virtual archive-member / bulk paths → searchable form.
  if (
    isArchiveMemberSearchablePath(p) ||
    /^data\/bulk\//i.test(p) ||
    (!p.startsWith('files/') && !/^https?:\/\//i.test(p) && !p.startsWith('s3://'))
  ) {
    return toSearchablePath(p).toLowerCase();
  }
  return p; // Preserve case — S3 object keys are case-sensitive.
};

/**
 * True when any URL on ``a`` matches any on ``b`` after storage-key normalization.
 */
const identityUrlsOverlap = (a: string[], b: string[]): boolean => {
  if (a.length === 0 || b.length === 0) return false;
  if (a.some((url) => b.includes(url))) return true;
  const aNorm = new Set(a.map(normalizeIdentityUrl).filter(Boolean));
  return b.some((url) => aNorm.has(normalizeIdentityUrl(url)));
};

/** True when two playable storage URLs refer to the same object key. */
const playableUrlsMatch = (
  a: string | undefined | null,
  b: string | undefined | null,
): boolean => {
  const left = (a ?? '').trim();
  const right = (b ?? '').trim();
  if (!left || !right) return false;
  if (left === right) return true;
  return normalizeIdentityUrl(left) === normalizeIdentityUrl(right);
};

/**
 * The source path a saved media row points at, read straight off ``mediaContext``.
 *
 * ``mediaContext`` is the only media field timeline-service round-trips, so for a
 * reloaded event this is the sole identity a processed rendition still shares
 * with its Displayed Sources row: ``mediaUrl`` names ``…/archive_processing/…``
 * (or the obfuscated copy of a moderated image) while the source row stores the
 * virtual ``…/archive.zip/member`` path, and the two share no substring. Without
 * it every archive-extract row reloads unmatched and gets a duplicate source row
 * invented for it (TR-2153).
 *
 * Deliberately read here instead of being restored onto ``media``: as a
 * top-level ``sourceUrl`` it outranks the playable ``mediaUrl`` in
 * ``getSrcToUse``, which points players at a bare relative path and hangs them
 * on a request that is never made.
 */
const mediaContextSourceIdentityUrls = (media: MediaSource): string[] =>
  collectIdentityUrls(
    typeof media.mediaContext?.sourceUrl === 'string'
      ? media.mediaContext.sourceUrl
      : undefined,
    typeof media.mediaContext?.sourceFilePath === 'string'
      ? media.mediaContext.sourceFilePath
      : undefined,
  );

/**
 * Match a media row to a source by display URL, shared archive-member path, or
 * shared citation content when both sides are content-only JSON.
 *
 * Zip/UFDR extracts often keep a virtual ``…/archive.zip/member`` on
 * ``sourceUrl`` while ``mediaUrl`` is the processed playable object. Matching
 * must include both the playable URL *and* the canonical source path that
 * ``buildEventMediaFromSources`` / ``toggleMediaSource`` stamp onto media rows.
 */
const mediaMatchesSourceIdentity = (
  media: MediaSource,
  source: MediaSource,
  preferredSourceUrl: string,
  peerMedia?: MediaSource[],
): boolean => {
  const mediaPreviewUrl =
    typeof media.mediaContext?.previewUrl === 'string'
      ? media.mediaContext.previewUrl
      : undefined;
  // Include media.sourceUrl / sourceFilePath — for archive extracts those are
  // often the only shared identity with the Selected Sources row after reload
  // (playable mediaUrl is not copied back onto the source).
  const mediaUrls = [
    ...collectIdentityUrls(
      media.mediaSrc,
      media.mediaUrl,
      media.sourceUrl,
      media.sourceFilePath,
      media.previewFilePath,
      media.dispatchedFilePath,
      media.s3Url,
      mediaPreviewUrl,
      media.originalUrl,
      typeof media.fileInfo?.searchablePath === 'string'
        ? media.fileInfo.searchablePath
        : undefined,
      typeof media.fileInfo?.fileUrl === 'string' ? media.fileInfo.fileUrl : undefined,
    ),
    // Survives the save round-trip, unlike the two top-level fields above.
    ...mediaContextSourceIdentityUrls(media),
  ];
  // Include citation-resolved mediaUrl/mediaSrc on the source. After
  // processCitationForAnswerItem, those are often the only proxyable URLs while
  // preferred / sourceUrl still point at a virtual archive-member path.
  const sourceUrls = collectIdentityUrls(
    source.sourceUrl,
    source.sourceFilePath,
    preferredSourceUrl,
    source.mediaUrl,
    source.mediaSrc,
    source.previewFilePath,
    source.dispatchedFilePath,
    source.s3Url,
    source.originalUrl,
    typeof source.fileInfo?.searchablePath === 'string'
      ? source.fileInfo.searchablePath
      : undefined,
    typeof source.fileInfo?.fileUrl === 'string' ? source.fileInfo.fileUrl : undefined,
  );
  const mediaKey = citationContentMatchKey(media);
  const sourceKey = citationContentMatchKey(source);
  const mediaPath = citationCanonicalSourcePath(media);
  const sourcePath =
    citationCanonicalSourcePath(source) || (preferredSourceUrl || '').trim();
  const pathsCompatible =
    !mediaPath ||
    !sourcePath ||
    mediaPath === sourcePath ||
    normalizeIdentityUrl(mediaPath) === normalizeIdentityUrl(sourcePath);
  const urlOverlap = identityUrlsOverlap(mediaUrls, sourceUrls);

  if (urlOverlap) {
    // Sibling JSON citations often share one dispatched file URL — require
    // matching excerpts when either side carries citation content.
    if (media.mediaType === 'json' && (mediaKey || sourceKey)) {
      if (mediaKey && sourceKey) {
        return mediaKey === sourceKey && pathsCompatible;
      }
      if (peerMedia?.length) {
        return uniqueLegacyJsonMediaMatch(
          media,
          source,
          preferredSourceUrl,
          peerMedia,
        );
      }
      return false;
    }
    return true;
  }

  // JSON media without URL overlap (content-only or parent sourceUrl on the
  // source vs file mediaUrl on media): match on excerpt + canonical path.
  if (media.mediaType !== 'json') {
    return false;
  }
  if (mediaKey && sourceKey && mediaKey === sourceKey) {
    return pathsCompatible;
  }
  if (peerMedia?.length) {
    return uniqueLegacyJsonMediaMatch(
      media,
      source,
      preferredSourceUrl,
      peerMedia,
    );
  }
  return false;
};

/**
 * The file-info lookup path used for content-moderation discovery.
 *
 * Mirrors ``resolveCmLookupPath`` in ``answer-item`` (the proven CM-discovery
 * path): archive / UFDR citations point at the *container* (e.g. a ``.zip``),
 * but CM is flagged on the extracted child. For container-like sources we
 * resolve the actually-cited extract path so the lookup hits the flagged
 * subdocument; non-container sources fall back to their own file path.
 */
const resolveSourceCmLookupKey = (
  source: MediaSource,
  fileInfo: FileSummary | null | undefined = source.fileInfo,
): string => {
  const preferred = resolvePreferredMediaDisplayForSource(source, fileInfo);
  const extractPath =
    isContainerLikeSource(source, fileInfo) &&
    (preferred.selectedBy === 'previewFilePath' || preferred.selectedBy === 'dispatchedFilePath')
      ? preferred.mediaUrl
      : undefined;
  const key =
    extractPath ||
    resolveFileInfoMediaDisplayUrl(fileInfo, source.s3Url || '') ||
    source.sourceFilePath ||
    source.sourceUrl ||
    '';
  // Never use a virtual ``…/archive.zip/inner`` path for CM file-info lookup.
  if (isArchiveMemberSearchablePath(key)) return '';
  return key;
};

/**
 * Clip identity for AV membership: both sides omit a trim bound (full-file), or
 * both carry the same normalized start/end. One-sided omission must not match
 * every trim of the same zip/playable file — unless
 * ``allowUntimedSourceToCoverTrim`` (hydration merge only) where a legacy
 * untimed source covers trimmed media.
 */
const mediaMembershipTimeMatch = (
  sourceTime: number | string | null,
  mediaTime: number | string | null,
  options?: { allowUntimedSourceToCoverTrim?: boolean },
): boolean => {
  if (sourceTime === null && mediaTime === null) return true;
  if (sourceTime === null) {
    return !!options?.allowUntimedSourceToCoverTrim;
  }
  if (mediaTime === null) return false;
  return sourceTime === mediaTime;
};

/** AV clip identity: matching start, then end (with legacy one-sided end tolerance). */
const mediaMembershipTrimMatch = (
  sourceStart: number | string | null,
  mediaStart: number | string | null,
  sourceEnd: number | string | null,
  mediaEnd: number | string | null,
  options?: {
    allowUntimedSourceToCoverTrim?: boolean;
    ignoreEndTime?: boolean;
  },
): boolean => {
  if (!mediaMembershipTimeMatch(sourceStart, mediaStart, options)) {
    return false;
  }
  if (options?.ignoreEndTime) return true;
  if (sourceEnd === null && mediaEnd === null) return true;
  if (sourceEnd !== null && mediaEnd !== null) {
    return sourceEnd === mediaEnd;
  }
  // Legacy add-to-media often copied start onto media without endTime (or the
  // reverse after reload) while identity + start still refer to the same clip.
  return true;
};

/** True when a source row carries a normalized AV trim bound. */
const sourceHasMembershipTrim = (source: MediaSource): boolean => {
  const ctx = getUnifiedSourceAnnotationContext(source);
  return (
    normalizeMediaMembershipTime(ctx.startTime) !== null ||
    normalizeMediaMembershipTime(ctx.endTime) !== null
  );
};

/**
 * True when ``media`` is the Selected Media counterpart of ``source``
 * (normalized identity + page/clip). Shared by green +/-, toggle remove, and
 * Selected Sources row delete so zip-member / legacy-time matches stay aligned.
 */
const mediaRowMatchesSourceMembership = (
  media: MediaSource,
  source: MediaSource,
  mediaList: MediaSource[],
  preferredOverride?: string,
  options?: {
    allowUntimedSourceToCoverTrim?: boolean;
    /** Pair trim edits to a source row before end time has synced onto it. */
    ignoreEndTime?: boolean;
  },
): boolean => {
  const sourceAnnotation = getUnifiedSourceAnnotationContext(source);
  const preferredDisplay = getPreferredSourceDisplay(source);
  const preferredSourceUrl =
    (preferredOverride || '').trim() || preferredDisplay.mediaUrl || '';
  const sourcePlayable = firstPlayableUrl(
    preferredOverride,
    source.mediaUrl,
    source.mediaSrc,
    source.s3Url,
    source.previewFilePath,
    source.dispatchedFilePath,
    preferredSourceUrl,
  );
  let sourcePage = resolveUnifiedSourcePageRef(source);
  if (preferredDisplay.mediaType === 'pdf' && !sourcePage) {
    sourcePage = 1;
  }
  const mediaPage = media.mediaContext?.pageNumber || media.mediaContext?.page;
  const sourceStartTime = normalizeMediaMembershipTime(sourceAnnotation.startTime);
  const mediaStartTime = normalizeMediaMembershipTime(
    media.mediaContext?.startTime,
  );
  const sourceEndTime = normalizeMediaMembershipTime(sourceAnnotation.endTime);
  const mediaEndTime = normalizeMediaMembershipTime(
    media.mediaContext?.endTime,
  );
  const mediaPreviewUrl =
    typeof media.mediaContext?.previewUrl === 'string'
      ? media.mediaContext.previewUrl
      : undefined;
  const mediaPlayable = firstPlayableUrl(
    media.mediaUrl,
    media.mediaSrc,
    media.s3Url,
    media.previewFilePath,
    media.dispatchedFilePath,
    mediaPreviewUrl,
  );
  // Prefer full identity (includes JSON excerpt guards). Playable-URL overlap
  // alone is only a fallback for zip/AV rows — never for JSON siblings that
  // share one dispatched file but differ by citation content.
  const identityFromUrls = mediaMatchesSourceIdentity(
    media,
    source,
    preferredSourceUrl || sourcePlayable,
    mediaList,
  );
  const mediaContentKey = citationContentMatchKey(media);
  const sourceContentKey = citationContentMatchKey(source);
  const jsonContentGuard =
    media.mediaType === 'json' && !!(mediaContentKey || sourceContentKey);
  const identityMatch =
    identityFromUrls ||
    (!jsonContentGuard && playableUrlsMatch(sourcePlayable, mediaPlayable));
  if (!identityMatch) return false;

  const sourceType = getMediaType(source, source.fileInfo);
  const mediaType = media.mediaType || getMediaType(media, media.fileInfo);
  const isAv =
    sourceType === 'audio' ||
    sourceType === 'video' ||
    mediaType === 'audio' ||
    mediaType === 'video';
  // AV clips ignore page markers (zip parents often carry a bogus page 1).
  const pageMatch =
    isAv ||
    (!sourcePage && !mediaPage) ||
    (sourcePage && mediaPage && sourcePage === mediaPage) ||
    (sourcePage === 1 && !mediaPage);
  const sourcePreviewUrl =
    source.previewFilePath || sourcePlayable || preferredSourceUrl;
  const mediaPreviewUrlForMatch =
    media.previewFilePath || mediaPreviewUrl || mediaPlayable;
  // JSON siblings sharing one dispatched URL still need preview separation; zip
  // / UFDR rows often match identity via normalized storage keys while raw
  // preview strings differ (``s3://…/files/…`` vs ``files/…``).
  const previewUrlMatch =
    identityFromUrls ||
    (!jsonContentGuard && playableUrlsMatch(sourcePlayable, mediaPlayable)) ||
    (!sourcePreviewUrl && !mediaPreviewUrlForMatch) ||
    !sourcePreviewUrl ||
    !mediaPreviewUrlForMatch ||
    playableUrlsMatch(sourcePreviewUrl, mediaPreviewUrlForMatch);
  // Pass 2 / legacy UI pairing: identity + untimed trim cover must not fail on
  // preview string mismatches (``s3://…`` vs ``files/…``) after reload.
  if (
    !previewUrlMatch &&
    !(options?.allowUntimedSourceToCoverTrim && identityMatch)
  ) {
    return false;
  }
  return (
    pageMatch &&
    (isAv
      ? mediaMembershipTrimMatch(
          sourceStartTime,
          mediaStartTime,
          sourceEndTime,
          mediaEndTime,
          options,
        )
      : mediaMembershipTimeMatch(sourceStartTime, mediaStartTime, options))
  );
};

/**
 * First trimmed sibling an untimed source covers under legacy rules — same
 * media-list order as ``mergeMissingMediaSourcesIntoList`` pass 2.
 */
const getLegacyUntimedMediaPeerForSource = (
  source: MediaSource,
  mediaList: MediaSource[],
  preferredOverride?: string,
): MediaSource | undefined => {
  for (const media of mediaList) {
    if (
      mediaRowMatchesSourceMembership(
        media,
        source,
        mediaList,
        preferredOverride,
        { allowUntimedSourceToCoverTrim: true },
      )
    ) {
      return media;
    }
  }
  return undefined;
};

/**
 * Ensure every media row has a Selected Sources counterpart (local list only —
 * does not write through to the parent, so opening an event stays clean).
 *
 * Exact membership first (identity + matching startTime). Then each still-
 * uncovered media may claim at most one unused untimed source (legacy
 * unsynced trim) so a single full-file source does not swallow every sibling
 * clip of the same zip/playable file.
 */
const mergeMissingMediaSourcesIntoList = (
  sources: MediaSource[],
  mediaList: MediaSource[] | undefined,
): MediaSource[] => {
  if (!mediaList?.length) return sources;
  const coveredMedia = new Set<number>();
  const usedSources = new Set<number>();

  for (let mi = 0; mi < mediaList.length; mi++) {
    for (let si = 0; si < sources.length; si++) {
      if (usedSources.has(si)) continue;
      if (
        mediaRowMatchesSourceMembership(mediaList[mi], sources[si], mediaList)
      ) {
        coveredMedia.add(mi);
        usedSources.add(si);
        break;
      }
    }
  }

  // Pass 2: each unused untimed source covers its first legacy trimmed sibling
  // only (same designation as ``sourcePairsWithMedia`` / green +/-).
  for (let si = 0; si < sources.length; si++) {
    if (usedSources.has(si)) continue;
    const peerMedia = getLegacyUntimedMediaPeerForSource(sources[si], mediaList);
    if (!peerMedia) continue;
    const mi = mediaList.indexOf(peerMedia);
    if (mi === -1 || coveredMedia.has(mi)) continue;
    coveredMedia.add(mi);
    usedSources.add(si);
  }

  const missing = mediaList
    .filter((_, mi) => {
      if (coveredMedia.has(mi)) return false;
      const media = mediaList[mi];
      // Last-chance: never synthesize when any untimed source already legacy-covers
      // this clip (including pass-2 used rows — do not skip usedSources here).
      for (let si = 0; si < sources.length; si++) {
        const legacySource = sources[si];
        const legacyCtx = getUnifiedSourceAnnotationContext(legacySource);
        const legacyHasTrim =
          normalizeMediaMembershipTime(legacyCtx.startTime) !== null ||
          normalizeMediaMembershipTime(legacyCtx.endTime) !== null;
        if (legacyHasTrim) continue;
        const designated = getLegacyUntimedMediaPeerForSource(
          legacySource,
          mediaList,
        );
        if (designated !== media) continue;
        if (
          !mediaRowMatchesSourceMembership(
            media,
            legacySource,
            mediaList,
            undefined,
            { allowUntimedSourceToCoverTrim: true },
          )
        ) {
          continue;
        }
        coveredMedia.add(mi);
        if (!usedSources.has(si)) usedSources.add(si);
        return false;
      }
      return true;
    })
    .map((media) => mediaItemToSelectedSource(media));
  const merged =
    missing.length === 0
      ? sources
      : deduplicateSources([...sources, ...getValidSources(missing)]);
  return merged;
};

/**
 * True when ``media`` is the Selected Media counterpart of ``source`` for UI
 * actions (green +/-, remove, reorder). Legacy untimed sources pair with the
 * first matching trimmed sibling (merge pass 2), not only when it is unique —
 * but they must not steal a clip already strictly owned by another source row
 * (adding the same file from Available Sources was lighting Hide Media without
 * creating a Displayed Media card).
 */
const sourcePairsWithMedia = (
  source: MediaSource,
  media: MediaSource,
  mediaList: MediaSource[],
  preferredOverride?: string,
  siblingSources?: MediaSource[],
): boolean => {
  if (
    mediaRowMatchesSourceMembership(
      media,
      source,
      mediaList,
      preferredOverride,
    )
  ) {
    return true;
  }
  if (
    !mediaRowMatchesSourceMembership(
      media,
      source,
      mediaList,
      preferredOverride,
      { allowUntimedSourceToCoverTrim: true },
    )
  ) {
    return false;
  }
  if (getLegacyUntimedMediaPeerForSource(source, mediaList, preferredOverride) !== media) {
    return false;
  }
  if (
    siblingSources?.some(
      (other) =>
        other !== source &&
        mediaRowMatchesSourceMembership(media, other, mediaList),
    )
  ) {
    return false;
  }
  return true;
};

/**
 * Assign each Displayed Media row to at most one Displayed Source (list order
 * wins). Strict trim matches claim first; legacy untimed rows cover leftovers.
 * Shared by reorder + ``isSourceInMedia`` so +/- / Hide Media stay aligned with
 * the media array (untimed full-file adds must not light Hide when a trimmed
 * sibling already owns the untimed media row).
 */
const assignMediaToSources = (
  orderedSources: MediaSource[],
  mediaList: MediaSource[],
): Array<MediaSource | undefined> => {
  if (!mediaList?.length) {
    return orderedSources.map(() => undefined);
  }
  const remainingMedia = [...mediaList];
  const mediaForSourceIndex: Array<MediaSource | undefined> = new Array(
    orderedSources.length,
  );

  orderedSources.forEach((source, sourceIndex) => {
    const matchIndex = remainingMedia.findIndex((media) =>
      mediaRowMatchesSourceMembership(media, source, mediaList),
    );
    if (matchIndex === -1) return;
    mediaForSourceIndex[sourceIndex] = remainingMedia[matchIndex];
    remainingMedia.splice(matchIndex, 1);
  });

  orderedSources.forEach((source, sourceIndex) => {
    if (mediaForSourceIndex[sourceIndex]) return;
    if (sourceHasMembershipTrim(source)) return;
    const peerMedia = getLegacyUntimedMediaPeerForSource(source, mediaList);
    if (!peerMedia) return;
    const matchIndex = remainingMedia.indexOf(peerMedia);
    if (matchIndex === -1) return;
    if (
      orderedSources.some(
        (other) =>
          other !== source &&
          mediaRowMatchesSourceMembership(peerMedia, other, mediaList),
      )
    ) {
      return;
    }
    mediaForSourceIndex[sourceIndex] = remainingMedia[matchIndex];
    remainingMedia.splice(matchIndex, 1);
  });

  return mediaForSourceIndex;
};

/**
 * Displayed Media rows owned by ``source`` under exclusive pairing. Hide Media
 * / row delete must drop only this row so an untimed sibling cannot delete a
 * trimmed clip it does not own.
 */
export const dropMediaOwnedBySource = (
  source: MediaSource,
  sources: MediaSource[],
  mediaList: MediaSource[],
): MediaSource[] => {
  if (!mediaList?.length) return mediaList ?? [];
  const sourceIndex = sources.indexOf(source);
  if (sourceIndex === -1) return mediaList;
  const owned = assignMediaToSources(sources, mediaList)[sourceIndex];
  if (!owned) return mediaList;
  return mediaList.filter((media) => media !== owned);
};

/**
 * Reorder Selected Media to follow Selected Sources without rebuilding rows.
 * Strict membership claims first so untimed + trimmed siblings cannot both
 * consume the same media row; unclaimed media is appended so nothing is dropped.
 */
const reorderMediaForSources = (
  orderedSources: MediaSource[],
  mediaList: MediaSource[],
): MediaSource[] => {
  if (!mediaList?.length) return [];
  const mediaForSourceIndex = assignMediaToSources(orderedSources, mediaList);
  const remainingMedia = [...mediaList];
  const orderedMedia: MediaSource[] = [];

  orderedSources.forEach((_, sourceIndex) => {
    const matched = mediaForSourceIndex[sourceIndex];
    if (!matched) return;
    const matchIndex = remainingMedia.indexOf(matched);
    if (matchIndex === -1) return;
    orderedMedia.push(matched);
    remainingMedia.splice(matchIndex, 1);
  });

  return [...orderedMedia, ...remainingMedia];
};

/**
 * Green +/- in Selected Sources: true when this row is currently in the event
 * ``media`` array (displayable as Selected Media). White plus = sources-only.
 */
export const isSourceInMedia = (
  source: MediaSource,
  mediaValue: MediaSource[],
  siblingSources?: MediaSource[],
): boolean => {
  if (!mediaValue?.length) return false;

  if (siblingSources?.length) {
    const sourceIndex = siblingSources.indexOf(source);
    if (sourceIndex === -1) {
      return mediaValue.some((media) =>
        sourcePairsWithMedia(source, media, mediaValue, undefined, siblingSources),
      );
    }
    return assignMediaToSources(siblingSources, mediaValue)[sourceIndex] !== undefined;
  }

  const preferredDisplay = getPreferredSourceDisplay(source);
  const preferredSourceUrl = preferredDisplay.mediaUrl || '';
  const sourceResolvedMediaUrl = (source.mediaUrl || source.mediaSrc || '').trim();
  const sourcePlayable = firstPlayableUrl(
    source.mediaUrl,
    source.mediaSrc,
    source.s3Url,
    source.previewFilePath,
    source.dispatchedFilePath,
    preferredSourceUrl,
  );
  // Preferred may be empty / archive-only while citation processing already
  // attached a proxyable mediaUrl — still allow membership checks in that case.
  const contentOnly =
    !preferredSourceUrl &&
    !sourceResolvedMediaUrl &&
    !!citationContentMatchKey(source);
  const hasJsonMedia =
    mediaValue.some(
      (m) =>
        m.mediaType === 'json' &&
        (!!(m.mediaUrl || m.mediaSrc) || !!citationContentMatchKey(m)),
    );
  // Zip/UFDR members often only keep a virtual ``sourceUrl`` after reload while
  // the playable URL lives solely on the media row — still probe for a match.
  const hasSourcePath = !!citationCanonicalSourcePath(source);
  // After reload, sources may lack citation content while JSON media still
  // holds the excerpt — still probe for a unique path/URL link.
  if (
    !preferredSourceUrl &&
    !sourceResolvedMediaUrl &&
    !sourcePlayable &&
    !contentOnly &&
    !hasJsonMedia &&
    !hasSourcePath
  ) {
    return false;
  }
  return mediaValue.some((media) =>
    sourcePairsWithMedia(source, media, mediaValue, undefined, siblingSources),
  );
};

const findPairedSourceIndexForMedia = (
  media: MediaSource,
  sources: MediaSource[],
  mediaList: MediaSource[],
  options?: { skipSourceIndices?: ReadonlySet<number> },
): number => {
  for (let index = 0; index < sources.length; index++) {
    if (options?.skipSourceIndices?.has(index)) continue;
    if (mediaRowMatchesSourceMembership(media, sources[index], mediaList)) {
      return index;
    }
  }
  let found = -1;
  for (let index = 0; index < sources.length; index++) {
    if (options?.skipSourceIndices?.has(index)) continue;
    const source = sources[index];
    const preferredSourceUrl = getPreferredSourceMediaUrl(source);
    const sourcePlayable = firstPlayableUrl(
      source.mediaUrl,
      source.mediaSrc,
      source.s3Url,
      source.previewFilePath,
      source.dispatchedFilePath,
      preferredSourceUrl,
    );
    const mediaPreviewUrl =
      typeof media.mediaContext?.previewUrl === 'string'
        ? media.mediaContext.previewUrl
        : undefined;
    const mediaPlayable = firstPlayableUrl(
      media.mediaUrl,
      media.mediaSrc,
      media.s3Url,
      media.previewFilePath,
      media.dispatchedFilePath,
      mediaPreviewUrl,
    );
    const identityMatch =
      mediaMatchesSourceIdentity(
        media,
        source,
        preferredSourceUrl || sourcePlayable,
        mediaList,
      ) || playableUrlsMatch(sourcePlayable, mediaPlayable);
    if (!identityMatch) continue;
    if (found !== -1) return -1;
    found = index;
  }
  return found;
};

const applyMediaCitationContextToSourceRow = (
  source: MediaSource,
  mediaContext: Record<string, unknown>,
): MediaSource => {
  const mergedSourceContext = mergeMediaCitationContextOntoSourceContext(
    source.sourceContext as Record<string, unknown> | undefined,
    mediaContext,
  );
  const rawMedia =
    mediaContext && typeof mediaContext === 'object' ? mediaContext : {};
  const mediaTouchedPageCitation =
    Object.prototype.hasOwnProperty.call(rawMedia, 'pageNumber')
    || Object.prototype.hasOwnProperty.call(rawMedia, 'page');
  const pageRef =
    mergedSourceContext.pageNumber ?? mergedSourceContext.page;
  const pageNumber =
    pageRef !== undefined && pageRef !== null && pageRef !== ''
      ? Number(pageRef)
      : undefined;
  const next: MediaSource = {
    ...source,
    sourceContext: mergedSourceContext,
    mediaContext: undefined,
  };
  if (pageNumber != null && Number.isFinite(pageNumber) && pageNumber > 0) {
    next.pageNumber = pageNumber;
  } else if (mediaTouchedPageCitation) {
    delete next.pageNumber;
    delete (next as { page?: string | number }).page;
  }
  return next;
};

/**
 * For each Selected Sources row paired with Selected Media, copy
 * ``media.mediaContext`` onto ``source.sourceContext`` so green +/- and
 * membership checks stay aligned after player edits.
 */
export const syncPairedSourceContextsFromMedia = (
  sources: MediaSource[],
  mediaList: MediaSource[],
): MediaSource[] => {
  if (!sources?.length) return sources ?? [];

  let nextSources = sources.map((source) => ({ ...source }));

  if (mediaList?.length) {
    const claimedSourceIndices = new Set<number>();
    for (const media of mediaList) {
      if (!media.mediaContext) continue;
      if (!shouldSyncMediaCitationOntoSource(
        media.mediaContext as Record<string, unknown>,
      )) {
        continue;
      }
      const sourceIndex = findPairedSourceIndexForMedia(
        media,
        nextSources,
        mediaList,
        { skipSourceIndices: claimedSourceIndices },
      );
      if (sourceIndex === -1) continue;
      claimedSourceIndices.add(sourceIndex);
      nextSources[sourceIndex] = applyMediaCitationContextToSourceRow(
        nextSources[sourceIndex],
        media.mediaContext as Record<string, unknown>,
      );
    }
  }

  nextSources = nextSources.map((source) => {
    if (!source.mediaContext) return source;
    if (!shouldSyncMediaCitationOntoSource(
      source.mediaContext as Record<string, unknown>,
    )) {
      return source;
    }
    return applyMediaCitationContextToSourceRow(
      source,
      source.mediaContext as Record<string, unknown>,
    );
  });

  return nextSources;
};

/** Event-modal player box: widen for side transcript, taller for below. */
const EVENT_MODAL_MEDIA_BELOW = { width: 360, height: 520 } as const;
const EVENT_MODAL_MEDIA_SIDE = {
  width: EVENT_MODAL_SIDE_CONTAINER_WIDTH_PX,
  height: 420,
} as const;
/** Prev/next media nav arrow (icon + a little breathing room), reserved on top of
 *  eventModalMediaBox.width so the arrows never eat into MediaDisplay's own budget. */
const EVENT_MODAL_NAV_ARROW_RESERVE_PX = 40;

/** Options for parent source/media writes from UnifiedSources. */
export type UnifiedSourcesChangeOptions = {
  /**
   * When false, the parent should absorb the update as hydration (e.g. mirroring
   * Selected Media into Selected Sources on open) without treating it as a user
   * edit / unsaved-changes prompt. Defaults to true.
   */
  markDirty?: boolean;
};

interface UnifiedSourcesProps {
  value: MediaSource[],
  mediaValue: MediaSource[],
  slide?: number,
  column?: string,
  isInModal?: boolean,
  isInPresentationView?: boolean,
  editingEnabled: boolean,
  caseId?: string,
  handleUnifiedSourcesChanges?: (
    sources: MediaSource[],
    media: MediaSource[],
    options?: UnifiedSourcesChangeOptions,
  ) => void,
  sourcesFromChat?: MediaSource[],
  mediaSourcesFromChat?: MediaSource[],
  isTitleSlide?: boolean,
  /** Persisted title-slide media URL from timeline patch (empty = sources-only). */
  titleSlidePersistedMediaUrl?: string,
  /**
   * ``stacked`` renders the Serenity Patterns Edit Event layout: Displayed
   * Media / Displayed Sources / Available Sources in one column, with no inline
   * player. ``legacy`` keeps the two-column sources + player layout used by the
   * remaining consumers.
   */
  layoutVariant?: 'legacy' | 'stacked',
}

type SourcePreviewList = 'available' | 'displayed';

function resolvePreviewSourceRow(
  list: SourcePreviewList,
  index: number,
  availableSources: MediaSource[],
  displayedSources: MediaSource[],
): MediaSource | undefined {
  const rows = list === 'displayed' ? displayedSources : availableSources;
  return rows?.[index];
}

const UnifiedSources = (props: UnifiedSourcesProps) => {
  const {
    value,
    mediaValue,
    isInModal,
    isInPresentationView,
    editingEnabled,
    caseId,
    handleUnifiedSourcesChanges,
    sourcesFromChat,
    mediaSourcesFromChat,
    isTitleSlide,
    titleSlidePersistedMediaUrl,
    layoutVariant = 'legacy',
  } = props;

  const isStacked = layoutVariant === 'stacked';
  const t = useTimePilotT();

  const [newSources, setNewSources] = useState<MediaSource[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [sourcesForResults, setSourcesForResults] = useState<MediaSource[]>([]);
  const [searchTerm] = useDebounce(inputValue, 500);
  const [selectedSources, setSelectedSources] = useState<number[]>([]);
  const [currentMediaIndex, setCurrentMediaIndex] = useState<number>(-1);
  const [dragPosition, setDragPosition] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const [availableEvidence, setAvailableEvidence] = useState<
    { url: string; mediaType: string; status: string; fileInfo: FileSummary }[]
  >([]);
  const [loadingEvidence, setLoadingEvidence] = useState(false);
  const [evidenceSources, setEvidenceSources] = useState<MediaSource[]>([]);
  const [sourceFromSessionStorage, setSourceFromSessionStorage] = useState<MediaSource | null>(null);
  const [previewPopoverOpen, setPreviewPopoverOpen] = useState(-1);
  const [sourceDetailsPopoverIndex, setSourceDetailsPopoverIndex] = useState<number | null>(null);
  const [previewSourceList, setPreviewSourceList] = useState<SourcePreviewList>('available');
  const [previewFileInfo, setPreviewFileInfo] = useState<FileSummary | null>(null);
  const [loadingPreviewFileInfo, setLoadingPreviewFileInfo] = useState(true);
  /** Enriched source for the open add-sources preview (tree fileStatusId resolve). */
  const [previewSourceOverride, setPreviewSourceOverride] = useState<MediaSource | null>(null);
  // CSAM preview toggle state for the open preview popover. Defaults to the
  // obfuscated (safe) view; switching to "Original" lazily fetches the
  // ``bypass_cm`` file-info so the audit log records the reveal at that moment.
  const [previewShowObfuscated, setPreviewShowObfuscated] = useState(true);
  const [previewOriginalFileInfo, setPreviewOriginalFileInfo] = useState<FileSummary | null>(null);
  const [loadingPreviewOriginal, setLoadingPreviewOriginal] = useState(false);
  // Tracks a failed ``bypass_cm`` fetch so the on-demand effect doesn't retry in
  // a loop (a failure leaves ``previewOriginalFileInfo`` null, which is otherwise
  // indistinguishable from "not fetched yet").
  const [previewOriginalFetchFailed, setPreviewOriginalFetchFailed] = useState(false);
  const [copiedToMediaShareIndex, setCopiedToMediaShareIndex] = useState<number | null>(null);
  // Content-moderation type keyed by the source's file-info lookup URL, so a CSAM
  // pill can be shown next to flagged sources in the event modal lists.
  const [cmTypeByKey, setCmTypeByKey] = useState<Map<string, string>>(new Map());

  const listRef = useRef<HTMLUListElement>(null);
  const addButtonRef = useRef<HTMLDivElement>(null);
  const previewPopoverRef = useRef<HTMLDivElement>(null);
  const addSourcePreviewDialogRef = useRef<HTMLDivElement>(null);
  const valueEffectRunRef = useRef(0);

  const {
    isFullscreen,
    setIsFullscreen,
    setFullscreenMediaItem,
    setFullscreenMediaEditing,
  } = useContext(FullScreenContext);
  const { idToken, accessToken } = useContext(AuthContext);
  const { cellId, accessToken: realAccessToken } = useCellServiceSession();
  const featuresContext = useContext(FeaturesContext);

  // ── Provenance files (evidence locker) ──────────────────────────────────
  const casesQuery = useCasesList();

  // caseId prop is the caseName from the nav dropdown ("Gabby Petito").
  // Provenance tags use caseTag which already includes the "case:" prefix
  // ("case:gabby petito") and is lowercased. Match case-insensitively.
  // All Cases (`*` / "All Cases") has no matching caseTag — omit the tags
  // filter so ``/files`` returns every file in the cell (same as Evidence Locker).
  const trimmedCaseId = caseId?.trim() ?? '';
  const isAllCasesSelected = trimmedCaseId === '*' || trimmedCaseId === 'All Cases';

  const caseTag = useMemo(() => {
    if (!trimmedCaseId || isAllCasesSelected) return null;
    return resolveCaseTag(casesQuery.data, trimmedCaseId);
  }, [trimmedCaseId, isAllCasesSelected, casesQuery.data]);

  const shouldLoadProvenanceFiles = isAllCasesSelected || caseTag !== null;
  // Labels are supplied for a single case too, not just All Cases: a file sitting
  // directly in a token-bearing case's folder shows that folder as its parent dir.
  const caseFolderLabelMap = useMemo(
    () => caseFolderLabels(casesQuery.data),
    [casesQuery.data],
  );
  const sourceListPathOptions: UnifiedSourceListPathOptions = useMemo(
    () => ({
      ...(isAllCasesSelected ? { includePathAfterBulk: true } : {}),
      caseFolderLabels: caseFolderLabelMap,
    }),
    [isAllCasesSelected, caseFolderLabelMap],
  );

  const debouncedSearchForFiles = useMemo(() => {
    if (!searchTerm || searchTerm.length <= 2) return undefined;
    if (searchTerm.startsWith('"') && searchTerm.endsWith('"')) return searchTerm.slice(1, -1);
    return searchTerm.trim();
  }, [searchTerm]);

  const filesQuery = useProvenanceFiles(
    {
      tags: caseTag ? [caseTag] : undefined,
      tagMatch: 'any',
      ...(debouncedSearchForFiles ? { searchQuery: debouncedSearchForFiles } : {}),
    },
    { enabled: shouldLoadProvenanceFiles },
  );
  // Flat /files omits subdocuments inside containers (zip, mbox, …). The
  // evidence locker surfaces those via /tree and /tree/search — mirror that
  // here so the edit-event picker lists extracted children the same way.
  const treeSearchQuery = useProvenanceTreeSearch({
    q: debouncedSearchForFiles,
    tags: caseTag ? [caseTag] : undefined,
    tagMatch: 'any',
    view: 'user',
    limit: 500,
    enabled: shouldLoadProvenanceFiles,
  });
  const featureFlags = featuresContext?.featureFlags || {};
  const { canSeeCsamTags, canRevealOriginal } = useCsamAccess();
  const jsonMediaDisplayEnabled = useJsonMediaDisplayEnabled();
  // Non-reviewers can never leave the obfuscated view: pin to `true` regardless of
  // the raw `previewShowObfuscated` state so a stale/false value can't reveal the
  // original — or trigger the audited `bypass_cm` fetch — in the source preview.
  // Every CM preview decision (bypass fetch, render URL, SourceCard prop) reads this.
  const effectivePreviewShowObfuscated = canRevealOriginal ? previewShowObfuscated : true;
  const mediaSourceShare = useMediaSourceShare();
  const registerApplyCaptureToEventMedia = useToolsDrawer()?.registerApplyCaptureToEventMedia;
  const registerAddSharedSourceToEvent = useToolsDrawer()?.registerAddSharedSourceToEvent;
  const updateMediaContextRef = useRef<
    ((field: string | object, value?: any) => void) | null
  >(null);
  const handleAddSourcesRef = useRef<(sourcesToAdd?: MediaSource[]) => void>(() => {});
  // Latest media/source state for trim edits from fullscreen (avoids stale closures while
  // the owner MediaDisplay is mounted-but-null and updates arrive via a ref).
  const mediaValueRef = useRef(mediaValue);
  mediaValueRef.current = mediaValue;
  const newSourcesRef = useRef(newSources);
  newSourcesRef.current = newSources;
  const currentMediaIndexRef = useRef(currentMediaIndex);
  currentMediaIndexRef.current = currentMediaIndex;
  const editingEnabledRef = useRef(editingEnabled);
  editingEnabledRef.current = editingEnabled;

  const patchMediaAtIndex = useCallback((index: number, next: MediaSource) => {
    const latestMedia = mediaValueRef.current ?? [];
    if (index < 0 || index >= latestMedia.length) return;
    const updatedMedia = latestMedia.map((item, i) => (i === index ? next : item));
    mediaValueRef.current = updatedMedia;
    handleUnifiedSourcesChanges?.(newSourcesRef.current, updatedMedia, { markDirty: false });
  }, [handleUnifiedSourcesChanges]);

  const { pdfCaptureHost: eventMediaPdfCaptureHost } = useEventMediaThumbnailBackfill({
    media: mediaValue,
    editingEnabled: editingEnabled && !!isInModal,
    caseId,
    cellId,
    accessToken: realAccessToken,
    tenantFriendlyName:
      typeof (featureFlags as Record<string, unknown>).tenantFriendlyName === 'string'
        ? ((featureFlags as Record<string, unknown>).tenantFriendlyName as string)
        : undefined,
    onPatchMedia: patchMediaAtIndex,
  });

  const availableSource = sessionStorage.getItem(`source`);

  const recordPreviewContentModeration = (
    source: MediaSource,
    fileInfo: FileSummary | null | undefined,
  ) => {
    if (!canSeeCsamTags) return;
    const cm = fileInfo?.contentModeration;
    const cmType = cm?.type && cm.findingStatus !== 'REVIEWED' ? cm.type : undefined;
    const key = resolveSourceCmLookupKey(source, fileInfo);
    if (!key || !cmType) return;
    setCmTypeByKey((previous) => {
      if (previous.get(key) === cmType) return previous;
      const next = new Map(previous);
      next.set(key, cmType);
      return next;
    });
  };

  const sourceContentModerationType = (source: MediaSource): string | undefined => {
    if (!canSeeCsamTags) return undefined;
    const cm = source.fileInfo?.contentModeration;
    if (cm?.type && cm.findingStatus !== 'REVIEWED') return cm.type;
    return cmTypeByKey.get(resolveSourceCmLookupKey(source));
  };

  useEffect(() => {
    const previewRow = resolvePreviewSourceRow(
      previewSourceList,
      previewPopoverOpen,
      sourcesForResults,
      newSources,
    );
    if (previewPopoverOpen === -1 || !previewRow?.sourceUrl?.trim()) {
      setPreviewFileInfo(null);
      setPreviewSourceOverride(null);
      setLoadingPreviewFileInfo(false);
      return;
    }

    const source = mergeTreeSourceForPreview(
      previewRow,
      treeSearchQuery.data,
    );
    let cancelled = false;
    setLoadingPreviewFileInfo(true);
    setPreviewFileInfo(null);
    setPreviewSourceOverride(null);

    const run = async () => {
      const playable = resolveFileInfoMediaDisplayUrl(source.fileInfo, source.s3Url || '');
      // Already have a real storage URL from the tree node — no file-info needed.
      if (playable) {
        if (!cancelled) {
          setPreviewSourceOverride({
            ...source,
            s3Url: playable,
            mediaUrl: playable,
            mediaSrc: playable,
          });
          setPreviewFileInfo(source.fileInfo ?? null);
          recordPreviewContentModeration(source, source.fileInfo);
          setLoadingPreviewFileInfo(false);
        }
        return;
      }

      const archiveMemberPath = source.sourceUrl || source.sourceFilePath || '';
      // A chat / legacy flat-list row can reference an archive member without
      // the tree result that supplies its processed URL. Search that member on
      // demand before falling back to the parent container listing; this is the
      // same provenance endpoint that powers Evidence Locker search and avoids
      // guessing a proxy URL from the virtual searchable path.
      if (
        isArchiveMemberSearchablePath(archiveMemberPath) &&
        cellId &&
        realAccessToken
      ) {
        const leaf = archiveMemberPath.split('/').filter(Boolean).pop() ?? '';
        if (leaf) {
          try {
            const nodes = await fetchProvenanceTreeSearch({
              cellId,
              accessToken: realAccessToken,
              q: leaf,
              tags: caseTag ? [caseTag] : undefined,
              tagMatch: 'any',
              view: 'user',
              limit: 100,
            });
            if (cancelled) return;
            const matchedNode = findTreeNodeForSourceUrl(nodes, archiveMemberPath);
            const matchedSource = matchedNode
              ? treeNodeToMediaSource(matchedNode)
              : null;
            const matchedPlayable = matchedSource
              ? resolveFileInfoMediaDisplayUrl(
                  matchedSource.fileInfo,
                  matchedSource.s3Url || '',
                )
              : '';
            if (matchedSource && matchedPlayable) {
              setPreviewSourceOverride({
                ...source,
                ...matchedSource,
                sourceUrl: source.sourceUrl || matchedSource.sourceUrl,
                sourceFilePath:
                  source.sourceFilePath || matchedSource.sourceFilePath,
                s3Url: matchedPlayable,
                mediaUrl: matchedPlayable,
                mediaSrc: matchedPlayable,
              });
              setPreviewFileInfo(matchedSource.fileInfo ?? null);
              recordPreviewContentModeration(matchedSource, matchedSource.fileInfo);
              setLoadingPreviewFileInfo(false);
              return;
            }
          } catch {
            // The parent-tree fallback below still handles rows with a known
            // fileStatusId if this targeted search is temporarily unavailable.
          }
        }
      }

      // Virtual ``…/archive.zip/inner`` paths cannot be resolved via
      // ``/files/file-info``. Re-list the parent container's tree children
      // (same as evidence-locker drill-in) to pick up the processed fileUrl.
      const containerFileStatusId =
        (typeof source.fileStatusId === 'string' && source.fileStatusId.trim()) ||
        (typeof source.fileInfo?.fileStatusId === 'string' &&
          source.fileInfo.fileStatusId.trim()) ||
        '';
      if (
        isArchiveMemberSearchablePath(archiveMemberPath) &&
        containerFileStatusId &&
        cellId &&
        realAccessToken
      ) {
        try {
          let nextOffset: number | null = 0;
          let match;
          while (nextOffset !== null && !match && !cancelled) {
            const page = await fetchProvenanceTreePage({
              cellId,
              accessToken: realAccessToken,
              fileStatusId: containerFileStatusId,
              view: 'user',
              offset: nextOffset,
              limit: 500,
            });
            if (cancelled) return;
            match = findTreeNodeForSourceUrl(page.items, source.sourceUrl || '');
            nextOffset = page.nextOffset;
          }
          const enriched = match ? treeNodeToMediaSource(match) : null;
          const enrichedPlayable = enriched
            ? resolveFileInfoMediaDisplayUrl(enriched.fileInfo, enriched.s3Url || '')
            : '';
          if (enriched) {
            const enrichedSource: MediaSource = {
              ...source,
              ...enriched,
              // Keep list identity on the virtual path; playable bytes on s3Url.
              sourceUrl: source.sourceUrl,
              sourceFilePath: source.sourceFilePath || source.sourceUrl,
              fileStatusId: containerFileStatusId,
              ...(enrichedPlayable
                ? {
                    s3Url: enrichedPlayable,
                    mediaUrl: enrichedPlayable,
                    mediaSrc: enrichedPlayable,
                  }
                : {}),
            };
            // Preserve the tree match's metadata even if provenance has not
            // produced a playable URL yet; it may still provide correct type,
            // status, CM information, and a parent fileStatusId for follow-up.
            setPreviewSourceOverride(enrichedSource);
            setPreviewFileInfo(enriched.fileInfo ?? null);
            recordPreviewContentModeration(enrichedSource, enriched.fileInfo);
          } else {
            setPreviewSourceOverride(source);
          }
        } catch {
          if (!cancelled) setPreviewSourceOverride(source);
        }
        if (!cancelled) setLoadingPreviewFileInfo(false);
        return;
      }

      // Top-level files: file-info lookup with a non-archive path is fine.
      const lookupUrl =
        (typeof source.s3Url === 'string' &&
        source.s3Url.trim() &&
        !isArchiveMemberSearchablePath(source.s3Url)
          ? source.s3Url.trim()
          : '') ||
        resolveDisplayLookupUrl(source, source.fileInfo, source.sourceUrl ?? '');
      if (!lookupUrl || isArchiveMemberSearchablePath(lookupUrl)) {
        if (!cancelled) {
          setPreviewSourceOverride(source);
          setLoadingPreviewFileInfo(false);
        }
        return;
      }
      try {
        const fileInfoObject = await getCurrentFileInfo(lookupUrl, cellId, realAccessToken);
        if (cancelled) return;
        setPreviewFileInfo(fileInfoObject);
        recordPreviewContentModeration(source, fileInfoObject);
        const fromInfo = resolveFileInfoMediaDisplayUrl(fileInfoObject, '');
        setPreviewSourceOverride(
          fromInfo
            ? { ...source, fileInfo: fileInfoObject, s3Url: fromInfo, mediaUrl: fromInfo, mediaSrc: fromInfo }
            : source,
        );
      } catch {
        if (!cancelled) setPreviewSourceOverride(source);
      }
      if (!cancelled) setLoadingPreviewFileInfo(false);
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    previewPopoverOpen,
    previewSourceList,
    sourcesForResults,
    newSources,
    treeSearchQuery.data,
    cellId,
    caseTag,
  ]);

  // Reset the CSAM preview toggle to the obfuscated (safe) view whenever a
  // different preview opens, and drop any previously fetched original.
  useEffect(() => {
    setPreviewShowObfuscated(true);
    setPreviewOriginalFileInfo(null);
    setLoadingPreviewOriginal(false);
    setPreviewOriginalFetchFailed(false);
  }, [previewPopoverOpen]);

  // When the user switches a flagged preview to "Original", fetch the
  // ``bypass_cm`` file-info on demand (records the audit reveal at that moment).
  useEffect(() => {
    if (previewPopoverOpen < 0 || effectivePreviewShowObfuscated || previewOriginalFileInfo) return;
    if (previewOriginalFetchFailed) return;
    if (!cellId || !realAccessToken) return;
    const source =
      previewSourceOverride ??
      resolvePreviewSourceRow(
        previewSourceList,
        previewPopoverOpen,
        sourcesForResults,
        newSources,
      );
    if (!source) return;
    const fileInfo = previewSourceOverride?.fileInfo ?? previewFileInfo ?? source.fileInfo;
    const key = resolveSourceCmLookupKey(source, fileInfo);
    const embeddedCm = fileInfo?.contentModeration;
    const cmType =
      canSeeCsamTags
        ? (embeddedCm?.type && embeddedCm.findingStatus !== 'REVIEWED'
            ? embeddedCm.type
            : cmTypeByKey.get(key))
        : undefined;
    if (!key || !cmType) return;
    let cancelled = false;
    setLoadingPreviewOriginal(true);
    getFileInfoFromServer(key, cellId, realAccessToken, true)
      .then((info) => {
        if (cancelled) return;
        setPreviewOriginalFileInfo(info ?? null);
        setLoadingPreviewOriginal(false);
      })
      .catch(() => {
        if (cancelled) return;
        setPreviewOriginalFileInfo(null);
        setPreviewOriginalFetchFailed(true);
        setLoadingPreviewOriginal(false);
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewPopoverOpen, previewSourceList, effectivePreviewShowObfuscated, previewOriginalFileInfo, previewOriginalFetchFailed, previewSourceOverride, previewFileInfo, sourcesForResults, newSources, cellId, realAccessToken, canSeeCsamTags, cmTypeByKey]);

  // Discover content moderation when the user opens a preview, rather than
  // fetching file-info for every row while the edit-event modal mounts. The
  // feature flag remains a full kill-switch and clears previous discoveries.
  useEffect(() => {
    if (!canSeeCsamTags) {
      setCmTypeByKey((prev) => (prev.size ? new Map() : prev));
    }
  }, [canSeeCsamTags]);

  useEffect(() => {
    if (availableSource) {
      const sourceFromSessionStorage = JSON.parse(availableSource);
      if (sourceFromSessionStorage.source) {
        sourceFromSessionStorage.source.caseId = sourceFromSessionStorage.caseId;
        setSourceFromSessionStorage(sourceFromSessionStorage.source);
      } else {
        setSourceFromSessionStorage(null);
      }
    } else {
      setSourceFromSessionStorage(null);
    }
  }, [availableSource]);

  const addSourceFromSessionStorage = () => {
    if (sourceFromSessionStorage) {
      const sourceType = extractPathAfterBulk(sourceFromSessionStorage.sourceFilePath || '').sourceType || 'Unknown';
      const sourceObject = {
        sourceType: sourceType,
        sourceUrl: sourceFromSessionStorage.sourceFilePath,
        truncatedFilePath: sourceFromSessionStorage.truncatedFilePath,
        sourceContext: sourceFromSessionStorage.sourceContext,
        pageNumber: sourceFromSessionStorage.pageNumber
      };
      handleAddSources([sourceObject]);
    }
  };

  // Sync hook loading state and results into local state vars used by the rest of the component.
  useEffect(() => {
    // Tree search augments the picker but must not block already-loaded flat
    // evidence (or hide it during a background tree refetch).
    setLoadingEvidence(filesQuery.isLoading || (!filesQuery.data && filesQuery.isFetching));
  }, [
    filesQuery.isLoading,
    filesQuery.isFetching,
    filesQuery.data,
  ]);

  useEffect(() => {
    if (!filesQuery.data) return;
    const allFiles = filesQuery.data.pages.flatMap(p => p.items) as FileSummary[];
    const mapped = allFiles.map(file => ({
      url: toProvenanceLookupPath(file.searchablePath),
      mediaType: file.extension ?? '',
      status: file.status ?? 'unknown',
      fileInfo: file,
    }));
    setAvailableEvidence(mapped);
  }, [filesQuery.data]);

  const provenanceStatusByPath = useMemo(() => {
    const statusByPath = new Map<string, string>();
    const allFiles = filesQuery.data?.pages.flatMap(p => p.items) as FileSummary[] | undefined;
    allFiles?.forEach((file) => {
      const status = file.status ?? 'unknown';
      [
        file.searchablePath,
        toProvenanceLookupPath(file.searchablePath),
        file.fileUrl,
        file.previewUrl,
        file.downloadUrl,
        file.transcriptUrl,
      ].forEach((candidate) => {
        const key = normalizeProvenanceStatusPath(candidate);
        if (key && !statusByPath.has(key)) {
          statusByPath.set(key, status);
        }
      });
    });
    treeSearchQuery.data?.forEach((node) => {
      const source = treeNodeToMediaSource(node);
      if (!source) return;
      const status = source.status ?? 'unknown';
      getSourceProvenanceStatusPathCandidates(source).forEach((candidate) => {
        const key = normalizeProvenanceStatusPath(candidate);
        if (key && !statusByPath.has(key)) {
          statusByPath.set(key, status);
        }
      });
    });
    return statusByPath;
  }, [filesQuery.data, treeSearchQuery.data]);

  const applyMatchedProvenanceStatus = (source: MediaSource): MediaSource => {
    const currentStatus = source.status?.trim().toLowerCase();
    if (currentStatus && currentStatus !== 'unknown') return source;
    const matchedStatus = getSourceProvenanceStatusPathCandidates(source)
      .map((candidate) => provenanceStatusByPath.get(candidate))
      .find((status): status is string => !!status);
    if (!matchedStatus || source.status === matchedStatus) return source;
    return { ...source, status: matchedStatus };
  };

  useEffect(() => {
    const fileSources: MediaSource[] = availableEvidence.map(evidence => ({
      sourceType: getMediaType({ sourceUrl: evidence.url } as MediaSource, evidence.fileInfo),
      sourceUrl: evidence.url,
      status: evidence.status || 'unknown',
      fileInfo: evidence.fileInfo,
      s3Url: evidence.fileInfo.fileUrl,
    }));
    const treeSources = treeSearchQuery.data
      ? treeNodesToMediaSources(treeSearchQuery.data)
      : [];
    // Keep top-level ``/files`` rows first in the unfiltered picker so tree
    // search hits cannot push ordinary evidence past its list limit. Flat rows
    // now retain their FileSummary metadata; tree-only archive members still
    // follow and retain their own embedded file-info / processed URL.
    setEvidenceSources(deduplicateSources([...fileSources, ...treeSources]));
  }, [availableEvidence, treeSearchQuery.data]);

  useEffect(() => {
    // Seed playable URLs and mirror Selected Media rows into the local Selected
    // Sources list. Keep this local-only so open/hydration does not mark the
    // event modal dirty (parent writes happen only on real user edits).
    //
    // Read media via ref so trim / mediaContext edits do not reset newSources
    // or re-run provenance / file-info / zip tree-search lookups.
    const mediaList = mediaValueRef.current;
    const prevSources = newSourcesRef.current;
    const uniqueSources = mergeMissingMediaSourcesIntoList(
      deduplicateSources(value).map((source) => {
        const prevRow = prevSources.find(
          (row) => getSourceIdentityKey(row) === getSourceIdentityKey(source),
        );
        return preserveLocalSourceEnrichment(source, prevRow, mediaList);
      }),
      mediaList,
    );
    setNewSources(uniqueSources);

    const runId = ++valueEffectRunRef.current;
    let cancelled = false;
    const enrichSources = async () => {
      const sourcesWithUrl = uniqueSources.filter((s) => !!s.sourceUrl);
      // Get unique sourceUrls so that we only fetch source info for each unique sourceUrl and not for each source object
      const uniqueSourceUrls = [...new Set(sourcesWithUrl.map((s) => s.sourceUrl!))];
      if (uniqueSourceUrls.length === 0) return;
      const enrichedByKey = new Map<string, SourcePlayableEnrichment>();
      const pendingFileInfo: Array<{
        sourceUrl: string;
        lookup: string;
        representative: MediaSource | undefined;
      }> = [];
      const recordFileInfoEnrichment = (
        sourceUrl: string,
        representative: MediaSource | undefined,
        fileInfo: FileSummary | null,
        lookup: string,
      ) => {
        const mediaType = getMediaType(
          representative ?? ({ mediaType: 'unknown', mediaUrl: sourceUrl } as MediaSource),
          fileInfo,
        );
        const playable = firstPlayableUrl(
          resolveFileInfoMediaDisplayUrl(fileInfo, ''),
          representative?.mediaUrl,
          representative?.mediaSrc,
          representative?.s3Url,
          lookup,
        );
        enrichedByKey.set(sourceUrl, {
          fileInfo: fileInfo ?? null,
          sourceType: fileInfo
            ? mediaType
            : uniqueSources.find((s) => s.sourceUrl === sourceUrl)?.sourceType,
          ...(playable
            ? { mediaUrl: playable, mediaSrc: playable, s3Url: playable }
            : {}),
        });
      };
      // Classify each source, then resolve file-info in one batch. Archive
      // members still use tree search; they are not stored objects.
      await Promise.all(
        uniqueSourceUrls.map(async (sourceUrl) => {
          const representative = uniqueSources.find((s) => s.sourceUrl === sourceUrl);
          // Existing provenance metadata is sufficient to classify and display
          // the source, but only when it describes this row rather than an
          // enclosing archive/container.
          const hasMatchingFileInfo = fileInfoMatchesSource(
            representative ?? ({ sourceUrl } as MediaSource),
            representative?.fileInfo,
          );
          if (representative?.fileInfo && hasMatchingFileInfo) {
            const playable = firstPlayableUrl(
              representative.mediaUrl,
              representative.mediaSrc,
              representative.s3Url,
              resolveFileInfoMediaDisplayUrl(representative.fileInfo, ''),
            );
            enrichedByKey.set(sourceUrl, {
              fileInfo: representative.fileInfo,
              sourceType: getMediaType(representative, representative.fileInfo),
              ...(playable
                ? { mediaUrl: playable, mediaSrc: playable, s3Url: playable }
                : {}),
            });
            return;
          }
          // Prefer an already-known processed URL; never file-info-lookup a
          // virtual ``…/archive.zip/inner`` searchable path.
          const lookup =
            resolveFileInfoMediaDisplayUrl(
              hasMatchingFileInfo ? representative?.fileInfo : undefined,
              representative?.s3Url || '',
            ) ||
            firstPlayableUrl(
              representative?.mediaUrl,
              representative?.mediaSrc,
              representative?.s3Url,
            ) ||
            resolveFullS3Url(representative ?? { sourceUrl }, sourceUrl);
          if (!lookup || isArchiveMemberSearchablePath(lookup)) {
            // Zip/UFDR members: resolve playable bytes via tree search (same as
            // the add-sources preview), or keep whatever seedSource already
            // borrowed from the media array / tree cache.
            let resolved = representative ?? ({ sourceUrl } as MediaSource);
            let playable = firstPlayableUrl(
              resolved.mediaUrl,
              resolved.mediaSrc,
              resolved.s3Url,
              resolveFileInfoMediaDisplayUrl(resolved.fileInfo, ''),
            );
            if (
              !playable &&
              isArchiveMemberSearchablePath(sourceUrl) &&
              cellId &&
              realAccessToken
            ) {
              const leaf = sourceUrl.split('/').filter(Boolean).pop() ?? '';
              if (leaf) {
                try {
                  const nodes = await fetchProvenanceTreeSearch({
                    cellId,
                    accessToken: realAccessToken,
                    q: leaf,
                    tags: caseTag ? [caseTag] : undefined,
                    tagMatch: 'any',
                    view: 'user',
                    limit: 100,
                  });
                  if (cancelled || runId !== valueEffectRunRef.current) return;
                  const matchedNode = findTreeNodeForSourceUrl(nodes, sourceUrl);
                  const matchedSource = matchedNode
                    ? treeNodeToMediaSource(matchedNode)
                    : null;
                  const matchedPlayable = matchedSource
                    ? resolveFileInfoMediaDisplayUrl(
                        matchedSource.fileInfo,
                        matchedSource.s3Url || '',
                      )
                    : '';
                  if (matchedSource && matchedPlayable) {
                    resolved = {
                      ...resolved,
                      ...matchedSource,
                      sourceUrl: resolved.sourceUrl || matchedSource.sourceUrl,
                      sourceFilePath:
                        resolved.sourceFilePath || matchedSource.sourceFilePath,
                    };
                    playable = matchedPlayable;
                  }
                } catch {
                  // Keep seeded media-borrow / tree-cache enrichment below.
                }
              }
            }
            enrichedByKey.set(sourceUrl, {
              fileInfo:
                resolved.fileInfo ??
                (hasMatchingFileInfo ? representative?.fileInfo ?? null : null),
              sourceType: playable
                ? getMediaType(
                    {
                      ...resolved,
                      mediaUrl: playable,
                      mediaSrc: playable,
                    } as MediaSource,
                    resolved.fileInfo,
                  )
                : representative?.sourceType,
              ...(playable
                ? { mediaUrl: playable, mediaSrc: playable, s3Url: playable }
                : {}),
            });
            return;
          }
          pendingFileInfo.push({ sourceUrl, lookup, representative });
        })
      );
      if (cancelled || runId !== valueEffectRunRef.current) return;
      if (pendingFileInfo.length > 0) {
        // One POST /files/file-info for the pending source list. Do not replace
        // this with Promise.all of getCurrentFileInfo. The per-URL GET below
        // runs only when the batch route returns null.
        const lookups = [...new Set(pendingFileInfo.map((item) => item.lookup))];
        const batched = await getFileInfoBatchFromServer(lookups, cellId, realAccessToken);
        if (cancelled || runId !== valueEffectRunRef.current) return;
        if (batched) {
          for (const item of pendingFileInfo) {
            recordFileInfoEnrichment(
              item.sourceUrl,
              item.representative,
              batched.get(item.lookup) ?? null,
              item.lookup,
            );
          }
        } else {
          await Promise.all(
            pendingFileInfo.map(async (item) => {
              const fileInfo = await getCurrentFileInfo(item.lookup, cellId, realAccessToken);
              if (cancelled || runId !== valueEffectRunRef.current) return;
              recordFileInfoEnrichment(
                item.sourceUrl,
                item.representative,
                fileInfo,
                item.lookup,
              );
            }),
          );
        }
      }
      if (cancelled || runId !== valueEffectRunRef.current) return;
      // Apply enrichment onto the live list (not the effect-start snapshot) so
      // rows the media-merge effect added while lookups ran are not wiped.
      setNewSources((prev) => {
        const applyEnrichment = (s: MediaSource): MediaSource => {
          if (!s.sourceUrl) return s;
          const enrichment = enrichedByKey.get(s.sourceUrl);
          if (!enrichment) return s;
          const playable = firstPlayableUrl(
            enrichment.mediaUrl,
            enrichment.mediaSrc,
            enrichment.s3Url,
            s.mediaUrl,
            s.mediaSrc,
            s.s3Url,
          );
          return {
            ...s,
            fileInfo: enrichment.fileInfo ?? s.fileInfo,
            sourceType: enrichment.sourceType ?? s.sourceType,
            ...(playable
              ? {
                  mediaUrl: playable,
                  mediaSrc: playable,
                  s3Url:
                    s.s3Url && !isArchiveMemberSearchablePath(s.s3Url)
                      ? s.s3Url
                      : playable,
                  mediaType:
                    s.mediaType && s.mediaType !== 'unknown'
                      ? s.mediaType
                      : enrichment.sourceType || s.mediaType,
                }
              : {}),
          };
        };
        const enriched = prev.map(applyEnrichment);
        return mergeMissingMediaSourcesIntoList(
          enriched,
          mediaValueRef.current,
        );
      });
    };
    enrichSources();
    return () => {
      cancelled = true;
    };
  }, [value, cellId, realAccessToken, caseTag]);

  // When Selected Media gains rows (or tree cache can seed playable URLs),
  // patch the local Selected Sources list without re-running provenance enrich.
  useEffect(() => {
    setNewSources((prev) => {
      if (!mediaValue?.length && prev.length === 0) return prev;
      const seededList =
        prev.length === 0
          ? prev
          : (() => {
              const seeded = prev.map((source) => {
                const alreadyPlayable = firstPlayableUrl(
                  source.mediaUrl,
                  source.mediaSrc,
                  source.s3Url,
                  resolveFileInfoMediaDisplayUrl(source.fileInfo, ''),
                );
                if (alreadyPlayable) return source;
                return seedSourceWithPlayableUrl(
                  source,
                  undefined,
                  mediaValue,
                );
              });
              return seeded.some((s, i) => s !== prev[i]) ? seeded : prev;
            })();
      // Value hydrate reads media via ref and does not re-run when media arrives
      // later — still mirror Selected Media into an empty/local list here.
      return mergeMissingMediaSourcesIntoList(seededList, mediaValue);
    });
  }, [mediaValue]);

  useEffect(() => {
    if (mediaValue && mediaValue.length > 0 && currentMediaIndex === -1) {
      // set the initial media index to the first media item
      setCurrentMediaIndex(0);
    }

    // Title-slide keeps a single source/media pair in the parent patch. Mark
    // hydration writes as non-dirty so open/cancel does not prompt to save.
    if (!isTitleSlide) return;

    const parentSources = value || [];
    const parentMedia = mediaValue || [];
    // Clear All / last-source remove writes empty sources+media to the parent
    // before this effect sees the matching newSources update. Remirroring from
    // stale local rows would resurrect the source and keep Available Sources hidden.
    if (parentSources.length === 0 && parentMedia.length === 0) return;
    const mediaAsSources = parentMedia.map(mediaItemToSelectedSource);
    // Prefer locally seeded/enriched newSources over sparse media→source
    // reshapes so markDirty:false hydration does not clobber fileInfo /
    // playable URLs that provenance enrich already attached.
    const sourcesToCompare = deduplicateSources([...newSources, ...mediaAsSources]);
    const validSources = getValidSources(sourcesToCompare);
    const deduplicatedSources = deduplicateSources(validSources);
    const onlySource = deduplicatedSources[0] || mediaAsSources[0];
    if (!onlySource) return;

    // Membership alone is not enough to skip: a sparse parent source can
    // "cover" media while local newSources already has fileInfo / playable
    // URLs from provenance enrich. Still hydrate when parent lacks that
    // enrichment so Save persists what the UI showed.
    const parentMatch = parentSources.find(
      (parent) =>
        getSourceIdentityKey(parent) === getSourceIdentityKey(onlySource) ||
        (parentMedia[0] &&
          mediaRowMatchesSourceMembership(
            parentMedia[0],
            parent,
            parentMedia,
          )),
    );
    const localPlayable = firstPlayableUrl(
      onlySource.mediaUrl,
      onlySource.mediaSrc,
      onlySource.s3Url,
      resolveFileInfoMediaDisplayUrl(onlySource.fileInfo, ''),
    );
    const parentPlayable = parentMatch
      ? firstPlayableUrl(
          parentMatch.mediaUrl,
          parentMatch.mediaSrc,
          parentMatch.s3Url,
          resolveFileInfoMediaDisplayUrl(parentMatch.fileInfo, ''),
        )
      : '';
    const parentHasEnrichmentParity =
      !!parentMatch &&
      (!onlySource.fileInfo || !!parentMatch.fileInfo) &&
      (!localPlayable || !!parentPlayable);
    const parentCoversMedia =
      parentMedia.length > 0 &&
      parentSources.length > 0 &&
      parentMedia.every((media) =>
        parentSources.some((source) =>
          mediaRowMatchesSourceMembership(media, source, parentMedia),
        ),
      );
    // TitleSlideModal ignores markDirty:false once sources/media are dirty, so
    // skipping here is only an optimization / anti-loop — require both
    // membership coverage and enrichment parity.
    if (parentCoversMedia && parentHasEnrichmentParity) return;
    const resolved = onlySource
      ? resolveSourceDisplayableMedia(
          onlySource,
          onlySource.fileInfo,
          jsonMediaDisplayEnabled,
        )
      : null;
    const displayUrl = resolved?.displayUrl || '';
    const s3Url = onlySource?.fileInfo?.fileUrl;
    const canKeepSource =
      !!onlySource &&
      !!resolved &&
      (resolved.hasDisplayableUrl || resolved.contentOnlyJson);
    const persistedTitleSlideMedia = (titleSlidePersistedMediaUrl ?? '').trim();
    const sourcesOnlyTitleSlide = persistedTitleSlideMedia === '';
    // Persisted empty media means sources-only — enrich sources but never
    // synthesize Selected Media (Save would persist media the user never chose).
    if (sourcesOnlyTitleSlide) {
      if (parentHasEnrichmentParity && !!parentMatch) return;
      if (canKeepSource) {
        handleUnifiedSourcesChanges?.([onlySource!], [], { markDirty: false });
      }
      return;
    }
    const newMediaSource: MediaSource | null = canKeepSource
      ? {
          mediaType: resolved!.mediaType,
          ...(displayUrl
            ? { mediaSrc: displayUrl, mediaUrl: displayUrl }
            : {}),
          content: onlySource!.content,
          sourceUrl: onlySource!.sourceUrl || onlySource!.sourceFilePath,
          sourceFilePath: onlySource!.sourceFilePath,
          ...(s3Url ? { s3Url, originalUrl: s3Url } : {}),
          fileInfo: onlySource!.fileInfo,
          mediaContext: buildMediaContextFromSource(
            onlySource!,
            {
              ...(resolved!.mediaType === 'audio' ||
              resolved!.mediaType === 'video'
                ? {}
                : (() => {
                    const sourcePage = resolveUnifiedSourcePageRef(onlySource!);
                    return sourcePage
                      ? { pageNumber: sourcePage, page: sourcePage }
                      : {};
                  })()),
              ...getUnifiedSourceTrimExtras(onlySource!),
              ...(onlySource!.sourceUrl &&
              displayUrl &&
              displayUrl !== onlySource!.sourceUrl
                ? { previewUrl: displayUrl }
                : {}),
            },
            resolved!.mediaType === 'audio' || resolved!.mediaType === 'video'
              ? { stripPageMarkers: true }
              : undefined,
          ),
          ...(resolved!.preferredDisplayable &&
          onlySource!.sourceUrl &&
          displayUrl &&
          displayUrl !== onlySource!.sourceUrl
            ? { previewFilePath: displayUrl }
            : {}),
        }
      : null;
    // Only write when we can keep a displayable pair. A failed zip/playable
    // resolve must not wipe parent sources/media under markDirty: false (that
    // advances the unsaved baseline and can persist an empty wipe on Save).
    if (canKeepSource && newMediaSource) {
      handleUnifiedSourcesChanges?.(
        [onlySource!],
        [newMediaSource],
        { markDirty: false },
      );
    }
  }, [
    value,
    newSources,
    mediaValue,
    isTitleSlide,
    jsonMediaDisplayEnabled,
    titleSlidePersistedMediaUrl,
  ]);

  useEffect(() => {
    if ((sourcesFromChat && sourcesFromChat.length > 0) || (evidenceSources && evidenceSources.length > 0) || (mediaSourcesFromChat && mediaSourcesFromChat.length > 0)) {
      setSourcesForResults([]);
      const sourcesFromMedia = mediaSourcesFromChat ? mediaSourcesFromChat.map((mediaSource) => {
        const hasMediaUrl = !!(mediaSource.mediaUrl || mediaSource.mediaSrc);
        const resolvedType = getMediaRowDisplayType(mediaSource, mediaSource.fileInfo);
        const identityUrl =
          mediaSource.sourceUrl ||
          mediaSource.sourceFilePath ||
          mediaSource.mediaUrl ||
          mediaSource.mediaSrc;
        return {
          sourceUrl: identityUrl,
          sourceFilePath: mediaSource.sourceFilePath || identityUrl,
          ...(hasMediaUrl
            ? {
                mediaUrl: mediaSource.mediaUrl || mediaSource.mediaSrc,
                mediaSrc: mediaSource.mediaSrc || mediaSource.mediaUrl,
              }
            : {}),
          mediaType: resolvedType,
          sourceType: resolvedType,
          content: mediaSource.content,
          sourceContext: mediaSource.mediaContext || mediaSource.sourceContext,
          fileInfo: mediaSource.fileInfo,
        } as MediaSource;
      }) : [];
      // Tree/evidence rows carry the processed ``fileInfo.fileUrl`` for
      // subdocuments. Put them ahead of legacy event rows, which can represent
      // the same archive member with only its virtual searchable path.
      const allSources = [ ...(evidenceSources || []), ...(sourcesFromChat || []), ...(sourcesFromMedia || []) ].map(
        applyMatchedProvenanceStatus,
      ) as MediaSource[];
      const filteredSources = allSources.filter((source) => {
        // Skip if source is already selected. Use full identity (incl. content
        // excerpt for content-only JSON) so sibling citations from the same
        // parent path remain available in the picker.
        const sourceIdentity = getSourceIdentityKey(source);
        if (newSources?.some((selectedSource) =>
          getSourceIdentityKey(selectedSource) === sourceIdentity
        )) {
          return false;
        }

        // Apply the unfiltered list cap after deduplication below. Capping
        // here lets duplicate/tree rows consume slots before flat evidence.
        if (!searchTerm || searchTerm.length <= 2) {
          return true;
        }

        if (!searchTerm) return false;
        let searchTermToUse = searchTerm;
        if (searchTermToUse.startsWith('"') && searchTermToUse.endsWith('"')) {
          searchTermToUse = searchTermToUse.slice(1, -1);
        } else {
          searchTermToUse = searchTermToUse.trim();
        }
        const sourceUrl = source.sourceUrl?.toLowerCase() || '';
        let sourceType = '';
        let sourceType2 = '';
        let sourceType3 = '';
        if (Array.isArray(source.sourceType) && source.sourceType[0]) {
          sourceType = source.sourceType[0].toLowerCase() || '';
          if (source.sourceType[1]) {
            sourceType = source.sourceType[1].toLowerCase() || '';
          }
          if (source.sourceType[2]) {
            sourceType = source.sourceType[2].toLowerCase() || '';
          }
        } else if (source.sourceType && typeof source.sourceType === "string") {
          sourceType = source.sourceType.toLowerCase() || '';
        }
        const pageNumber = source.sourceContext?.pageNumber?.toString() || '';
        let searchStr = searchTermToUse.toLowerCase();
        // remove psuedoRegEx modifiers
        if(searchStr.startsWith('^')) {
          searchStr = searchStr.slice(1);
        } else if(searchStr.endsWith('$')) {
          searchStr = searchStr.slice(0, -1);
        }

        return sourceUrl.includes(searchStr) ||
               sourceType.includes(searchStr) ||
               sourceType2.includes(searchStr) ||
               sourceType3.includes(searchStr) ||
               pageNumber.includes(searchStr);
      });
      const deduplicated = deduplicateSources(filteredSources);
      // Keep the initial picker bounded, but only after duplicates are removed.
      // Preserve the flat-file-first ordering while reserving slots for archive
      // members that are available only from the provenance tree.
      const treeSourceKeys = new Set(
        (treeSearchQuery.data ? treeNodesToMediaSources(treeSearchQuery.data) : []).map(
          getSourceIdentityKey,
        ),
      );
      const flatSourceKeys = new Set(
        availableEvidence.map((evidence) =>
          getSourceIdentityKey({
            sourceUrl: evidence.url,
            sourceType: getMediaType({ sourceUrl: evidence.url } as MediaSource, evidence.fileInfo),
          } as MediaSource),
        ),
      );
      const treeOnlySources = deduplicated.filter(
        (source) =>
          treeSourceKeys.has(getSourceIdentityKey(source)) &&
          !flatSourceKeys.has(getSourceIdentityKey(source)),
      );
      const nonTreeOnlySources = deduplicated.filter(
        (source) =>
          !treeSourceKeys.has(getSourceIdentityKey(source)) ||
          flatSourceKeys.has(getSourceIdentityKey(source)),
      );
      const treeOnlyLimit = Math.min(TREE_ONLY_SOURCES_LIST_RESERVE, treeOnlySources.length);
      const initialPickerSources = [
        ...nonTreeOnlySources.slice(0, SOURCES_LIST_LIMIT - treeOnlyLimit),
        ...treeOnlySources.slice(0, treeOnlyLimit),
      ];
      setSourcesForResults(
        !searchTerm || searchTerm.length <= 2
          ? initialPickerSources
          : deduplicated,
      );
    } else {
      setSourcesForResults([]);
    }
  }, [
    sourcesFromChat,
    evidenceSources,
    searchTerm,
    mediaSourcesFromChat,
    newSources,
    provenanceStatusByPath,
    treeSearchQuery.data,
    availableEvidence,
  ]);

  /*
  const handleMediaChanges = (mediaOnly: MediaSource[], currentMediaIndex?: number) => {
    if (handleUnifiedSourcesChanges) {
      const newSourcesCopy: MediaSource[] = [];
      let index = 0;
      newSources.forEach(source => {
        const updatedSource = { ...source };
        if (isSourceInMedia(source, mediaValue, newSources)) {
          if (index === currentMediaIndex) {
            updatedSource.sourceContext = { ...mediaOnly[index].mediaContext };
            updatedSource.pageNumber = mediaOnly[index].mediaContext?.pageNumber;
          }
          index++;
        }
        newSourcesCopy.push(updatedSource);
      });
      handleUnifiedSourcesChanges(newSourcesCopy, mediaOnly);
    }
  }
  */

  const handleReorderSources = (event: any, info: any, index: number) => {
    if (!editingEnabled) return;

    const draggedIndex = index;
    const targetIndex = dragPosition !== null ? dragPosition : Math.round(index + info.offset.y / 40);

    if (draggedIndex === targetIndex) {
      setDragPosition(null);
      setIsDragging(false);
      return;
    }

    const newOrder = [...newSources];
    const [draggedItem] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);

    // Reorder Selected Media by carrying over existing rows — do not rebuild
    // from preferred display. Zip/UFDR sources can be in-media via a shared
    // virtual path while the playable URL lives only on the media row;
    // reconstructing would write empty/archive-member URLs and wipe the viewer.
    const newOrderMedia = reorderMediaForSources(newOrder, mediaValue || []);
    if (handleUnifiedSourcesChanges) {
      handleUnifiedSourcesChanges(newOrder, newOrderMedia);
    }
    setDragPosition(null);
    setIsDragging(false);

    // Keep the blue color for 5 seconds after dropping
    const targetItemIndex = newOrder.findIndex(item =>
      item.sourceUrl === draggedItem.sourceUrl &&
      item.sourceContext?.pageNumber === draggedItem.sourceContext?.pageNumber
    );

    if (targetItemIndex !== -1) {
      setDraggedItemIndex(targetItemIndex);
      setTimeout(() => {
        setDraggedItemIndex(null);
      }, 500);
    }
  }

  const removeSource = (index: number) => {
    const sourceToRemove = newSources[index];
    const lessSources = newSources.filter((_, i) => i !== index);
    const deduplicatedSources = deduplicateSources(lessSources);

    // Drop the paired Selected Media row(s) with the same membership rules as
    // +/- / toggleMediaSource (zip-member identity + normalized trim times).
    // Do not gate on isMediaSource — archive rows may only match via playable
    // URL borrowed from media.
    let deduplicatedMedia = mediaValue;
    if (sourceToRemove && mediaValue?.length) {
      const updatedMedia = dropMediaOwnedBySource(
        sourceToRemove,
        newSources,
        mediaValue,
      );
      // Media identity is URL/content based — never run source-keyed
      // deduplicateSources here (shared parent sourceUrl would collapse siblings).
      deduplicatedMedia = dedupeMediaSources(updatedMedia);
    }
    // Title slide hides Displayed Sources and gates Available Sources on local
    // occupancy — update immediately. Event modal waits on the parent echo so
    // its value-hydrate / media-merge path is unchanged.
    if (isTitleSlide) {
      setNewSources(deduplicatedSources);
      if ((deduplicatedMedia?.length ?? 0) === 0) {
        setCurrentMediaIndex(-1);
      }
    }
    handleUnifiedSourcesChanges?.(deduplicatedSources, deduplicatedMedia);
  }

  const toggleMediaSource = (source: MediaSource) => {
    const inMedia = isSourceInMedia(source, mediaValue, newSources);
    // Allow remove when identity matches media even if the source row has not
    // been enriched with a playable URL yet (common for zip members on reload).
    if (!inMedia && !isMediaSource(source, source.fileInfo, jsonMediaDisplayEnabled)) {
      return;
    }

    const sourcePage = resolveUnifiedSourcePageRef(source);
    const trimExtras = getUnifiedSourceTrimExtras(source);
    const {
      preferredDisplay,
      preferredDisplayable,
      displayUrl,
      mediaType,
    } = resolveSourceDisplayableMedia(source, source.fileInfo, jsonMediaDisplayEnabled);
    const sourcePlayable = firstPlayableUrl(
      displayUrl,
      source.mediaUrl,
      source.mediaSrc,
      source.s3Url,
      source.previewFilePath,
      source.dispatchedFilePath,
    );

    if (inMedia) {
      // Drop only the exclusively owned media row so Hide Media matches +/-.
      const updatedMedia = dropMediaOwnedBySource(source, newSources, mediaValue);
      handleUnifiedSourcesChanges?.(newSources, updatedMedia);
    } else {
      // Add to media - include the start time in the media context
      const canonicalSourceUrl = source.sourceUrl || source.sourceFilePath;
      const newMediaSource: MediaSource = {
        mediaType,
        ...(displayUrl ? { mediaSrc: displayUrl, mediaUrl: displayUrl } : {}),
        content: source.content,
        sourceFilePath: source.sourceFilePath,
        sourceUrl: canonicalSourceUrl,
        fileInfo: source.fileInfo,
        mediaContext: buildMediaContextFromSource(
          source,
          {
            ...(mediaType === 'audio' || mediaType === 'video'
              ? {}
              : { pageNumber: sourcePage, page: sourcePage }),
            ...trimExtras,
            ...(displayUrl && canonicalSourceUrl && displayUrl !== canonicalSourceUrl
              ? { previewUrl: displayUrl }
              : {}),
          },
          mediaType === 'audio' || mediaType === 'video'
            ? { stripPageMarkers: true }
            : undefined,
        ),
        ...(preferredDisplayable && preferredDisplay.selectedBy === 'previewFilePath'
          ? { previewFilePath: preferredDisplay.mediaUrl }
          : preferredDisplayable && preferredDisplay.selectedBy === 'dispatchedFilePath'
            ? { dispatchedFilePath: preferredDisplay.mediaUrl }
            : displayUrl && displayUrl !== canonicalSourceUrl
              ? { previewFilePath: displayUrl }
              : {}),
      };
      handleUnifiedSourcesChanges?.(newSources, [...mediaValue, newMediaSource]);
    }
  }

  const getSourceForMedia = (media: MediaSource) => {
    // Read ref-backed lists so same-tick updateMediaContext bursts (enter-edit /
    // Save / Cancel) see the startTime already written on the prior call.
    // Render-scoped newSources/mediaValue stay stale until the next render.
    const sources = newSourcesRef.current ?? [];
    const mediaList = mediaValueRef.current ?? [];
    // Prefer exact membership (identity + matching startTime) so sibling trims
    // of the same zip/playable file sync to the correct Selected Sources row.
    for (let index = 0; index < sources.length; index++) {
      if (
        mediaRowMatchesSourceMembership(media, sources[index], mediaList)
      ) {
        return index;
      }
    }
    // Legacy / unsynced: untimed source covering trimmed media. Require a
    // unique match so sibling clips cannot steal each other's updates.
    let legacyFound = -1;
    for (let index = 0; index < sources.length; index++) {
      if (
        mediaRowMatchesSourceMembership(
          media,
          sources[index],
          mediaList,
          undefined,
          { allowUntimedSourceToCoverTrim: true },
        )
      ) {
        if (legacyFound !== -1) return -1;
        legacyFound = index;
      }
    }
    if (legacyFound !== -1) return legacyFound;

    // Last resort: unique identity/playable match when trim fields are still
    // out of sync (title-slide first trim, legacy reload). Require a single
    // candidate so sibling clips of the same file cannot steal updates.
    let found = -1;
    for (let index = 0; index < sources.length; index++) {
      const source = sources[index];
      const preferredSourceUrl = getPreferredSourceMediaUrl(source);
      const sourcePlayable = firstPlayableUrl(
        source.mediaUrl,
        source.mediaSrc,
        source.s3Url,
        source.previewFilePath,
        source.dispatchedFilePath,
        preferredSourceUrl,
      );
      const mediaPreviewUrl =
        typeof media.mediaContext?.previewUrl === 'string'
          ? media.mediaContext.previewUrl
          : undefined;
      const mediaPlayable = firstPlayableUrl(
        media.mediaUrl,
        media.mediaSrc,
        media.s3Url,
        media.previewFilePath,
        media.dispatchedFilePath,
        mediaPreviewUrl,
      );
      const identityMatch =
        mediaMatchesSourceIdentity(
          media,
          source,
          preferredSourceUrl || sourcePlayable,
          mediaList,
        ) || playableUrlsMatch(sourcePlayable, mediaPlayable);
      if (!identityMatch) continue;
      if (found !== -1) return -1; // ambiguous among sibling clips
      found = index;
    }
    return found;
  };

  const findSourceIndexForMediaSync = (media: MediaSource): number => {
    const direct = getSourceForMedia(media);
    if (direct !== -1) return direct;
    const sources = newSourcesRef.current ?? [];
    const mediaList = mediaValueRef.current ?? [];
    let found = -1;
    for (let index = 0; index < sources.length; index++) {
      if (
        !mediaRowMatchesSourceMembership(
          media,
          sources[index],
          mediaList,
          undefined,
          { allowUntimedSourceToCoverTrim: true, ignoreEndTime: true },
        )
      ) {
        continue;
      }
      if (found !== -1) return -1;
      found = index;
    }
    return found;
  };

  const updateMediaContext = (field: string | object, value?: any) => {
    // Last hop of a capture save: an upload can report success and still leave
    // nothing on screen if the patch is dropped here, so both give-up paths are
    // logged under the same scope as the upload stages.
    const fields = typeof field === 'string' ? [field] : Object.keys(field ?? {});
    if (!editingEnabledRef.current) {
      logCaptureSave(
        'warn',
        'media-context write dropped: this Displayed Media list is not editable',
        { fields: fields.join(','), mediaIndex: currentMediaIndexRef.current },
      );
      return;
    }
    const latestMedia = mediaValueRef.current ?? [];
    const latestSources = newSourcesRef.current ?? [];
    const mediaIndex = currentMediaIndexRef.current;
    if (mediaIndex < 0 || mediaIndex >= latestMedia.length) {
      logCaptureSave('warn', 'media-context write dropped: no media row selected', {
        fields: fields.join(','),
        mediaIndex,
        mediaCount: latestMedia.length,
      });
      return;
    }

    const fieldUpdates = (typeof field === 'string' ? { [field]: value } : field) as Record<string, unknown>;
    const canvasUpdate =
      fieldUpdates.canvas && typeof fieldUpdates.canvas === 'object' && !Array.isArray(fieldUpdates.canvas)
        ? fieldUpdates.canvas as Record<string, unknown>
        : undefined;
    const updatesStillFrameThumbnail =
      Object.prototype.hasOwnProperty.call(fieldUpdates, 'stillFrame') ||
      (canvasUpdate !== undefined &&
        (Object.prototype.hasOwnProperty.call(canvasUpdate, 'stillFrame') ||
          Object.prototype.hasOwnProperty.call(canvasUpdate, 'stillFrameImage')));
    const updatesPdfThumbnailCapture =
      Object.prototype.hasOwnProperty.call(fieldUpdates, 'capture') &&
      canvasUpdate !== undefined &&
      Object.prototype.hasOwnProperty.call(canvasUpdate, 'image');
    const skipSourceContextWrite = updatesStillFrameThumbnail || updatesPdfThumbnailCapture;
    const isTrimContextUpdate =
      Object.prototype.hasOwnProperty.call(fieldUpdates, 'startTime') ||
      Object.prototype.hasOwnProperty.call(fieldUpdates, 'endTime');
    let sourceIndex = getSourceForMedia(latestMedia[mediaIndex]);
    if (sourceIndex === -1 && isTrimContextUpdate && !skipSourceContextWrite) {
      sourceIndex = findSourceIndexForMediaSync(latestMedia[mediaIndex]);
    }
    let updatedSources: MediaSource[] = [...latestSources];
    if (sourceIndex !== -1 && !skipSourceContextWrite) {
      const sourceForMedia = { ...latestSources[sourceIndex] };
      if (typeof field === 'string') {
        sourceForMedia.sourceContext = { ...sourceForMedia.sourceContext, [field as string]: value, lastUpdated: Date.now() };
      } else {
        sourceForMedia.sourceContext = { ...sourceForMedia.sourceContext, ...field, lastUpdated: Date.now() };
      }
      updatedSources = latestSources.slice(0, sourceIndex);
      updatedSources.push(sourceForMedia);
      updatedSources.push(...latestSources.slice(sourceIndex + 1));
    }
    const updatedMedia = latestMedia.map((media, index) => {
      if (index === mediaIndex) {
        if (typeof field === 'string') {
          return { ...media, mediaContext: { ...media.mediaContext, [field]: value, lastUpdated: Date.now() } };
        } else {
          return { ...media, mediaContext: { ...media.mediaContext, ...field, lastUpdated: Date.now() } };
        }
      }
      return media;
    });
    // Keep refs current within the same tick so multi-field edit bursts (enter edit /
    // Save / fullscreen flush) don't clobber each other via a stale mediaValue closure.
    mediaValueRef.current = updatedMedia;
    newSourcesRef.current = updatedSources;
    if (updatesStillFrameThumbnail || updatesPdfThumbnailCapture) {
      // A write of ``capture: ''`` blanks the tile even when the still ref is
      // present, because resolveCompactPreview treats it as "Use Full Media" and
      // returns before reading canvas.stillFrameImage. Distinguish the two so a
      // clear landing after a successful upload is visible.
      const emptyString = (value: unknown) =>
        typeof value === 'string' && value.trim() === '';
      const clearsCapture = emptyString(fieldUpdates.capture);
      logCaptureSave('info', 'media-context applied', {
        fields: fields.join(','),
        mediaIndex,
        hasChangeHandler: Boolean(handleUnifiedSourcesChanges),
        clearsCapture,
        clearsStillFrameImage: emptyString(canvasUpdate?.stillFrameImage),
        ...(clearsCapture && isCaptureSaveLogEnabled()
          ? { calledFrom: captureSaveCallerStack() }
          : {}),
      });
    }
    handleUnifiedSourcesChanges?.(updatedSources, updatedMedia);
  }

  updateMediaContextRef.current = updateMediaContext;

  useEffect(() => {
    if (!registerApplyCaptureToEventMedia) return;
    if (!editingEnabled || !isInModal || !mediaValue?.length) {
      registerApplyCaptureToEventMedia(null);
      return;
    }
    registerApplyCaptureToEventMedia((imageRef: string) => {
      updateMediaContextRef.current?.("capture", imageRef);
    });
    return () => registerApplyCaptureToEventMedia(null);
  }, [registerApplyCaptureToEventMedia, editingEnabled, isInModal, mediaValue?.length]);

  const handleAddSources = (sourcesToAdd?: MediaSource[]) => {
    const selectedSourcesToAdd = sourcesToAdd || selectedSources.map((index) => sourcesForResults[index]);
    let updatedSources = [...newSources, ...selectedSourcesToAdd];
    if (isTitleSlide) {
      if (newSources.length > 0 || (mediaValue?.length ?? 0) > 0) return;
      updatedSources = updatedSources.slice(0,1);
    }
    if (isTitleSlide) {
      const onlySource = updatedSources[0];
      // Add to media - include the start time in the media context
      const titleMediaType = getMediaType(onlySource, onlySource.fileInfo);
      const titleIsAv =
        titleMediaType === 'audio' || titleMediaType === 'video';
      const titleSourcePage = resolveUnifiedSourcePageRef(onlySource);
      const newMediaSource: MediaSource = {
        mediaType: titleMediaType,
        mediaSrc: onlySource.sourceUrl,
        mediaUrl: onlySource.sourceUrl,
        mediaContext: buildMediaContextFromSource(
          onlySource,
          {
            ...(titleIsAv
              ? {}
              : titleSourcePage
                ? { pageNumber: titleSourcePage, page: titleSourcePage }
                : {}),
            ...getUnifiedSourceTrimExtras(onlySource),
          },
          titleIsAv ? { stripPageMarkers: true } : undefined,
        ),
      };
      updatedSources = deduplicateSources(updatedSources);
      handleUnifiedSourcesChanges?.(updatedSources, [newMediaSource]);
    } else {
      updatedSources = deduplicateSources(updatedSources);
      handleUnifiedSourcesChanges?.(updatedSources, mediaValue);
    }
    // Clear selected sources after adding
    setSelectedSources([]);
  }

  handleAddSourcesRef.current = handleAddSources;

  useEffect(() => {
    if (!registerAddSharedSourceToEvent) return;
    if (!editingEnabled || !isInModal) {
      registerAddSharedSourceToEvent(null);
      return;
    }
    registerAddSharedSourceToEvent((source: MediaSource) => {
      handleAddSourcesRef.current([source]);
    });
    return () => registerAddSharedSourceToEvent(null);
  }, [registerAddSharedSourceToEvent, editingEnabled, isInModal]);

  const handleRemoveAllSources = () => {
    if (isTitleSlide) {
      setNewSources([]);
      setCurrentMediaIndex(-1);
    }
    handleUnifiedSourcesChanges?.([], []);
  }

  /** Clear All on Displayed Media drops the media rows and leaves sources intact.
   *  Title slide has no Displayed Sources well — clearing media also drops the
   *  paired source so Available Sources can show again. */
  const handleClearAllMedia = () => {
    if (isTitleSlide) {
      handleRemoveAllSources();
      return;
    }
    handleUnifiedSourcesChanges?.(newSources, []);
    setCurrentMediaIndex(-1);
  };

  /** Add All pulls in every source currently listed under Available Sources. */
  const handleAddAllSources = () => {
    if (!sourcesForResults?.length) return;
    handleAddSources(isTitleSlide ? sourcesForResults.slice(0, 1) : sourcesForResults);
  };

  const removeMediaAtIndex = (index: number) => {
    if (isTitleSlide) {
      handleRemoveAllSources();
      return;
    }
    const updatedMedia = mediaValue.filter((_, i) => i !== index);
    handleUnifiedSourcesChanges?.(newSources, updatedMedia);
    if (currentMediaIndex >= updatedMedia.length) {
      setCurrentMediaIndex(Math.max(0, updatedMedia.length - 1));
    }
  };

  /**
   * Displayed Media opens the app-level fullscreen viewer in standard viewing.
   * There is no inline player in the stacked layout to own the handoff, so this
   * writes FullScreenContext directly — the same entry the source-card maximize
   * uses — and registers the editing bridge so a later capture lands on this
   * row's mediaContext.
   */
  const openThumbnailEditor = (index: number) => {
    const media = mediaValue?.[index];
    if (!media) return;
    // updateMediaContext resolves its target from the ref, which is only
    // refreshed on render; stamp it now so a capture applied before the next
    // render still writes to the row the user opened.
    setCurrentMediaIndex(index);
    currentMediaIndexRef.current = index;

    const fullscreenItem = resolveMediaForDisplay(media);
    setFullscreenMediaItem(fullscreenItem);
    setFullscreenMediaEditing({
      isEditing: editingEnabled,
      thumbnailEditMode: true,
      updateMediaContext: (field: string | object, value?: any) => {
        updateMediaContextRef.current?.(field, value);
        setFullscreenMediaItem((prev) => {
          if (!prev) return prev;
          const patch = typeof field === 'string' ? { [field]: value } : field;
          return {
            ...prev,
            mediaContext: { ...prev.mediaContext, ...patch, lastUpdated: Date.now() },
          };
        });
      },
    });
    setIsFullscreen(true);
  };

  const calculateDragPosition = (event: any, draggedIndex: number) => {
    if (!listRef.current || !editingEnabled) return;
    const listRect = listRef.current.getBoundingClientRect();
    const listItems = listRef.current.getElementsByTagName('li');
    // Calculate the position relative to the list
    const clientY = event.clientY || event.touches?.[0]?.clientY;
    if (!clientY) return;
    const relativeY = clientY - listRect.top;
    // If we're dragging outside the list bounds, maintain the previous position
    if (relativeY < 0 || relativeY > listRect.height) {
      return;
    }
    // Find the position between items
    let position = 0;
    for (let i = 0; i < listItems.length; i++) {
      const itemRect = listItems[i].getBoundingClientRect();
      const itemTop = itemRect.top - listRect.top;
      const itemBottom = itemRect.bottom - listRect.top;
      const itemMiddle = itemTop + (itemRect.height / 2);
      // Add a dead zone around the middle of each item
      const deadZoneSize = 10; // pixels
      const deadZoneTop = itemMiddle - deadZoneSize;
      const deadZoneBottom = itemMiddle + deadZoneSize;
      // If we're in the dead zone of the current item, maintain the previous position
      if (relativeY >= deadZoneTop && relativeY <= deadZoneBottom) {
        return;
      }
      // If we're above the middle of the current item, place before it
      if (relativeY < itemMiddle) {
        position = i;
        break;
      }
    }
        // If we're past the last item, set position to the end
    if (position === 0 && relativeY > listRect.height) {
      position = listItems.length;
    }
    // Adjust position to not show above or directly below the dragged item
    if (position === draggedIndex || position === draggedIndex + 1) {
      return;
    }
    setDragPosition(position);
  }

  // Beside is the default when nothing is saved (matches resolveTranscriptPosition's
  // policy everywhere else) — a raw strict-equality check against the unresolved
  // mediaContext value kept the box at the narrow "below" width for every unset
  // preference, which in turn tripped the narrow-container collapse and made side
  // layout unreachable by default (TR-1589 follow-up).
  const eventModalTranscriptBeside =
    resolveTranscriptPosition({
      mediaContextPosition: mediaValue?.[currentMediaIndex]?.mediaContext?.transcriptPosition,
    }) === 'side';
  // Shared with the arrows+MediaDisplay row below so the outer column's max-width
  // and the row's actual intended width never disagree (Bugbot follow-up, TR-1589).
  const eventModalShowLeftArrow = !isFullscreen && (mediaValue?.length ?? 0) > 1;
  const eventModalShowRightArrow = (mediaValue?.length ?? 0) > 1;
  const eventModalArrowReservePx =
    (eventModalShowLeftArrow ? EVENT_MODAL_NAV_ARROW_RESERVE_PX : 0) +
    (eventModalShowRightArrow ? EVENT_MODAL_NAV_ARROW_RESERVE_PX : 0);
  // Thumbnail strip is wider than the legacy below player (360) whenever there are
  // enough chips — without matching the player row to that strip, below-transcript
  // video/audio leaves a dead band on the right of Selected Media.
  const eventModalThumbStripWidthPx = (mediaValue?.length ?? 0) >= 3 ? 470 : 375;
  const eventModalMediaBox = eventModalTranscriptBeside
    ? EVENT_MODAL_MEDIA_SIDE
    : {
        width: Math.max(
          EVENT_MODAL_MEDIA_BELOW.width,
          eventModalThumbStripWidthPx - eventModalArrowReservePx,
        ),
        height: EVENT_MODAL_MEDIA_BELOW.height,
      };
  // Plain inline pixel max-width, not a Tailwind arbitrary-value class: a
  // `max-w-[${...}px]` template literal is never a complete class-name token in this
  // file's source text, so Tailwind's JIT scanner can't statically find it and never
  // generates the CSS — the cap silently never applied. For beside, size to the side
  // container (+ arrows + slack). For below, hug the player row / thumb strip so the
  // column does not keep a percentage `max-w-1/2` that fights `width: fit-content`
  // ancestors in EventModal (Bugbot follow-up, TR-1589).
  const eventModalPlayerRowWidthPx =
    eventModalMediaBox.width + eventModalArrowReservePx;
  const eventModalOuterMaxWidthPx = eventModalTranscriptBeside
    ? EVENT_MODAL_SIDE_CONTAINER_WIDTH_PX + eventModalArrowReservePx + 50
    : Math.max(eventModalPlayerRowWidthPx, eventModalThumbStripWidthPx);

  const eventModalPlayerRowRef = useRef<HTMLDivElement>(null);
  const [eventModalPlayerRowMeasuredWidthPx, setEventModalPlayerRowMeasuredWidthPx] =
    useState<number | null>(null);

  useEffect(() => {
    if (!isInModal || newSources?.length === 0) {
      setEventModalPlayerRowMeasuredWidthPx(null);
      return;
    }
    const el = eventModalPlayerRowRef.current;
    if (!el) return;

    const measure = () => {
      const width = Math.floor(el.getBoundingClientRect().width);
      if (width > 0) {
        setEventModalPlayerRowMeasuredWidthPx(width);
      }
    };

    measure();
    const observer = new ResizeObserver(() => measure());
    observer.observe(el);
    return () => observer.disconnect();
  }, [
    isInModal,
    newSources?.length,
    mediaValue?.length,
    eventModalTranscriptBeside,
    eventModalPlayerRowWidthPx,
    eventModalArrowReservePx,
  ]);

  const eventModalEffectiveMediaBox = useMemo(() => {
    if (!isInModal || eventModalPlayerRowMeasuredWidthPx == null) {
      return eventModalMediaBox;
    }
    const availableMediaWidth = Math.max(
      120,
      Math.min(
        eventModalMediaBox.width,
        eventModalPlayerRowMeasuredWidthPx - eventModalArrowReservePx,
      ),
    );
    if (availableMediaWidth >= eventModalMediaBox.width) {
      return eventModalMediaBox;
    }
    const scale = availableMediaWidth / eventModalMediaBox.width;
    return {
      width: Math.floor(availableMediaWidth),
      height: Math.max(200, Math.floor(eventModalMediaBox.height * scale)),
    };
  }, [
    isInModal,
    eventModalMediaBox,
    eventModalPlayerRowMeasuredWidthPx,
    eventModalArrowReservePx,
  ]);

  useEffect(() => {
    if (isInModal || previewPopoverOpen === -1) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (previewPopoverRef.current && !previewPopoverRef.current.contains(event.target as Node)) {
        setPreviewPopoverOpen(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [previewPopoverOpen, isInModal]);

  const openSourcePreview = useCallback((list: SourcePreviewList, index: number) => {
    setPreviewSourceList(list);
    setPreviewPopoverOpen(index);
  }, []);

  const closeAddSourcePreview = useCallback(() => {
    setPreviewPopoverOpen(-1);
  }, []);

  useEffect(() => {
    if (!isInModal || previewPopoverOpen < 0) return;
    const frame = requestAnimationFrame(() => {
      addSourcePreviewDialogRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [isInModal, previewPopoverOpen]);

  useEffect(() => {
    if (!isInModal || previewPopoverOpen < 0) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      closeAddSourcePreview();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isInModal, previewPopoverOpen, closeAddSourcePreview]);

  const renderAddSourcePreviewPanel = (layout: 'popover' | 'dialog') => {
    const sourceObject = resolvePreviewSourceRow(
      previewSourceList,
      previewPopoverOpen,
      sourcesForResults,
      newSources,
    );
    if (previewPopoverOpen < 0 || !sourceObject) return null;
    const resolvedPreviewSource =
      previewSourceOverride ?? sourceObject;
    const csamCmType = canSeeCsamTags
      ? sourceContentModerationType(resolvedPreviewSource)
      : undefined;
    const cmPreviewActive = !!csamCmType;
    const showOriginalPreview =
      cmPreviewActive && !effectivePreviewShowObfuscated;
    const embeddedFileInfo = resolvedPreviewSource.fileInfo ?? null;
    const fetchedLooksLikeContainer =
      !!previewFileInfo &&
      isContainerLikeSource(
        {
          sourceFilePath:
            previewFileInfo.fileUrl ||
            resolvedPreviewSource.sourceUrl ||
            '',
        },
        previewFileInfo,
      );
    const embeddedHasPlayableUrl = !!(
      embeddedFileInfo?.fileUrl?.startsWith('s3://') ||
      embeddedFileInfo?.fileUrl?.startsWith('files/') ||
      embeddedFileInfo?.previewUrl
    );
    const basePreviewFileInfo =
      fetchedLooksLikeContainer && embeddedHasPlayableUrl
        ? embeddedFileInfo
        : (previewFileInfo ?? embeddedFileInfo);
    const previewFileInfoEffective = showOriginalPreview
      ? (previewOriginalFileInfo ?? basePreviewFileInfo)
      : basePreviewFileInfo;
    const previewSourceRow: MediaSource = {
      ...resolvedPreviewSource,
      sourceUrl: resolvedPreviewSource.sourceUrl,
      sourceFilePath:
        resolvedPreviewSource.sourceFilePath ||
        resolvedPreviewSource.sourceUrl,
      fileInfo: previewFileInfoEffective ?? undefined,
      s3Url:
        resolvedPreviewSource.s3Url ??
        previewFileInfoEffective?.fileUrl ??
        resolvedPreviewSource.fileInfo?.fileUrl,
      originalUrl:
        resolvedPreviewSource.originalUrl ||
        resolvedPreviewSource.sourceUrl,
    };
    const addSourceDisplayable =
      !loadingPreviewFileInfo
        ? resolveSourceDisplayableMedia(
            previewSourceRow,
            previewFileInfoEffective,
            jsonMediaDisplayEnabled,
          )
        : null;
    const addSourcePreferredDisplay =
      addSourceDisplayable?.preferredDisplay ?? null;
    const addSourceResolvedMediaType: MediaType | null =
      addSourceDisplayable?.mediaType ?? null;
    const addSourceSelectedByCitation =
      !!addSourceDisplayable?.preferredDisplayable &&
      (addSourcePreferredDisplay?.selectedBy === 'previewFilePath' ||
        addSourcePreferredDisplay?.selectedBy === 'dispatchedFilePath');
    const originalPreviewUrl =
      previewOriginalFileInfo?.previewUrl ??
      previewOriginalFileInfo?.fileUrl ??
      null;
    const playableFromFileInfo = resolveFileInfoMediaDisplayUrl(
      previewFileInfoEffective,
      '',
    );
    const preferredUrl = showOriginalPreview
      ? (originalPreviewUrl ?? '')
      : (addSourceDisplayable?.displayUrl ?? '');
    const previewDisplayUrl =
      (playableFromFileInfo &&
      preferredUrl &&
      !preferredUrl.startsWith('s3://') &&
      !preferredUrl.startsWith('files/')
        ? playableFromFileInfo
        : preferredUrl) ||
      playableFromFileInfo ||
      (addSourceDisplayable?.hasDisplayableUrl
        ? resolvedPreviewSource.s3Url ||
          resolvedPreviewSource.sourceUrl ||
          ''
        : '');
    const canShowAddSourceMediaPreview =
      !!addSourceDisplayable &&
      (addSourceDisplayable.hasDisplayableUrl ||
        addSourceDisplayable.contentOnlyJson) &&
      addSourceResolvedMediaType !== null &&
      (!previewDisplayUrl ||
        !isArchiveMemberSearchablePath(previewDisplayUrl)) &&
      (addSourceDisplayable.contentOnlyJson ||
        (!!previewDisplayUrl &&
          isDisplayableMediaType(addSourceResolvedMediaType, {
            jsonMediaDisplay: jsonMediaDisplayEnabled,
          })));

    const panelShellClassName =
      layout === 'dialog'
        ? 'relative flex min-h-0 flex-1 flex-col gap-2 outline-none h-full w-full'
        : 'relative flex flex-col justify-start items-center gap-2 w-[450px] h-[650px] outline-none';
    const mediaShellClassName =
      layout === 'dialog'
        ? 'previewContainer flex min-h-0 flex-1 w-full min-w-0 items-stretch justify-center'
        : 'previewContainer flex flex-row justify-center items-center w-[420px] min-w-[420px] h-[540px] min-h-[540px]';

    return (
      <div
        ref={previewPopoverRef}
        tabIndex={0}
        className={panelShellClassName}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            closeAddSourcePreview();
          }
        }}
      >
        <Button
          type="button"
          iconOnly
          size="sm"
          style="text"
          color="light"
          background="inverse"
          className="absolute top-1 right-1 z-10"
          onClick={(e) => {
            e.stopPropagation();
            closeAddSourcePreview();
          }}
          aria-label={t('sources.closePreview')}
        >
          <XIcon />
        </Button>
        <div className="box-border flex flex-row items-center justify-center gap-1 px-10 w-full shrink-0">
          <div className={`text-white/100 text-sm break-all bg-gray-900 rounded-sm my-1 p-1 text-center ${PATH_DISPLAY_WHITESPACE_CLASSNAME}`}>
            {extractPathAfterBulk(sourceObject.truncatedFilePath || sourceObject.sourceUrl || 'Unknown', true).truncatedFilePath}
          </div>
        </div>
        {loadingPreviewFileInfo ? (
          <div className="flex flex-col items-center justify-center gap-4 h-full min-h-[200px] pb-8">
            <Spinner size="lg" color="white" className="opacity-70" />
            <div className="animate-pulse font-bold">{t('sources.loadingInfo')}</div>
          </div>
        ) : showOriginalPreview &&
          (loadingPreviewOriginal ||
            (!originalPreviewUrl &&
              !previewOriginalFetchFailed)) ? (
          <div className="flex flex-col items-center justify-center gap-4 h-full min-h-[200px] pb-8">
            <Spinner size="lg" color="white" className="opacity-70" />
            <div className="animate-pulse font-bold">{t('sources.loadingOriginal')}</div>
          </div>
        ) : showOriginalPreview && !originalPreviewUrl ? (
          <div className="flex h-full min-h-[200px] items-center justify-center p-6 text-center text-sm text-white/80">
            {t('sources.originalFailed')}
          </div>
        ) : !canShowAddSourceMediaPreview ? (
          <div className="flex h-full min-h-[200px] items-center justify-center p-6 text-center text-sm text-white/80">
            {t('sources.noPreview')}
          </div>
        ) : (
          <div className={mediaShellClassName}>
            <MediaDisplay
              mediaItem={{
                fileInfo: previewFileInfoEffective,
                mediaType: addSourceResolvedMediaType,
                ...(showOriginalPreview
                  ? {
                      mediaUrl: originalPreviewUrl ?? '',
                      mediaSrc: originalPreviewUrl ?? '',
                    }
                  : previewDisplayUrl
                    ? {
                        mediaUrl: previewDisplayUrl,
                        mediaSrc: previewDisplayUrl,
                      }
                    : {}),
                content: sourceObject.content,
                mediaContext: sourceObject.sourceContext,
                ...(showOriginalPreview
                  ? (originalPreviewUrl
                      ? { previewFilePath: originalPreviewUrl }
                      : {})
                  : addSourceSelectedByCitation && previewDisplayUrl
                    ? { previewFilePath: previewDisplayUrl }
                    : {}),
              } as MediaSource}
              containerHeight={layout === 'dialog' ? undefined : 520}
              containerWidth={layout === 'dialog' ? undefined : 420}
              fillContainer={layout === 'dialog'}
              isPreview={true}
              caseId={caseId}
              {...(cmPreviewActive
                ? {
                    contentModerationType: csamCmType,
                    showObfuscatedPreview: effectivePreviewShowObfuscated,
                    ...(canRevealOriginal
                      ? { onToggleObfuscatedPreview: setPreviewShowObfuscated }
                      : {}),
                  }
                : {})}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`box-border flex w-full min-w-0 max-w-full ${
        isStacked ? `flex-col ${STACKED_SOURCES_SECTIONS_GAP_CLASS}` : 'flex-col sm:flex-row gap-8'
      }`}
    >
      {/* Sources List - Left Side (Displayed Sources + Available Sources when stacked) */}
      <div
        className={`box-border flex flex-col justify-left min-w-0 max-w-full ${
          isStacked ? `order-2 w-full ${STACKED_SOURCES_SECTIONS_GAP_CLASS}` : 'sm:max-w-1/2 sm:flex-1'
        }`}
      >
        {!(isTitleSlide && isStacked) && (
        <div className={isStacked ? STACKED_SOURCES_SECTION_SHELL_CLASS : 'contents'}>
        {isStacked ? (
          <SourcesSectionHeader
            title={t('sources.displayed')}
            count={newSources?.length ?? 0}
            tooltip={t('sources.displayedTooltip')}
            actionLabel={newSources?.length > 0 ? t('sources.clearAll') : undefined}
            onAction={newSources?.length > 0 ? handleRemoveAllSources : undefined}
          />
        ) : (
          <div className="box-border flex items-center justify-between gap-2 m-0 p-0 mb-[6px] mr-[10px]">
            <div className="box-border flex items-center gap-2">
              <label className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0">
                {t('sources.selected', { count: newSources?.length ?? 0 })}
              </label>
              <TpTooltip content={t('sources.selectedTooltip')} placement="top" delayDuration={0}>
                <InfoIcon size={16} className="text-white/65 cursor-help" />
              </TpTooltip>
            </div>
            {newSources?.length > 0 && (
              <Button
                style="text"
                color="accent"
                background="inverse"
                size="sm"
                className="text-brand-blue-light/80 text-sm font-semibold"
                onClick={handleRemoveAllSources}
              >
                {t('sources.removeAll')}
              </Button>
            )}
          </div>
        )}
        <motion.ul
          ref={listRef}
          className={
            isStacked
              ? STACKED_DISPLAYED_SOURCES_LIST_CLASS
              : 'box-border flex flex-col space-y-2 w-full p-2 m-0 sm:max-h-[225px] overflow-auto custom-scrollbar2 mt-1 list-decimal list-outside border-1 border-white/10 border-solid rounded-md bg-white/5'
          }
        >
          {newSources?.length === 0 && (
            isStacked ? (
              <div className="px-2">
                <SourcesEmptyState>
                  {t('sources.addFromAvailable')}
                </SourcesEmptyState>
              </div>
            ) : (
              <div className="text-white text-sm text-center py-4 border-1 border-white/10 border-dashed rounded-md">
                {t('sources.noneSelected')}
              </div>
            )
          )}
          {newSources && newSources?.length > 0 && newSources.map((sourceObject: MediaSource, index: number) => {
            const uniqueKey = sourceObject.sourceUrl ?
              `${sourceObject.sourceUrl}-${index}` :
              `source-${index}`;
            const csamCmType = canSeeCsamTags
              ? sourceContentModerationType(sourceObject)
              : undefined;
            const sourceListModifier = formatUnifiedSourceListAnnotation(sourceObject, t);
            const sourceListPath = formatUnifiedSourceListPath(sourceObject, sourceListPathOptions, t);

            return (
              <React.Fragment key={uniqueKey}>
                {dragPosition === index && (
                  <motion.li
                    className="h-[2px] bg-brand-blue my-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  />
                )}
                <motion.li
                  drag={editingEnabled}
                  dragConstraints={{ top: 0, bottom: 0, left: 0, right: 0 }}
                  dragDirectionLock={true}
                  dragElastic={0}
                  dragMomentum={false}
                  dragTransition={{
                    bounceStiffness: 100,
                    bounceDamping: 10
                  }}
                  initial={false}
                  whileDrag={{
                    scale: 1.02,
                    backgroundColor: "rgba(255,255,255,0.1)",
                    boxShadow: "0px 5px 10px rgba(0,0,0,0.2)",
                    cursor: "grabbing",
                    zIndex: 50
                  }}
                  animate={{
                    backgroundColor: "transparent",
                    scale: 1,
                    boxShadow: "none",
                    zIndex: 0,
                    x: 0
                  }}
                  transition={{
                    duration: 0.2
                  }}
                  onDragStart={() => {
                    setIsDragging(true);
                    setDragPosition(null);
                    setDraggedItemIndex(index);
                  }}
                  onDrag={(event) => {
                    calculateDragPosition(event, index);
                  }}
                  onDragEnd={(event, info) => handleReorderSources(event, info, index)}
                  className={
                    isStacked
                      ? 'box-border flex flex-row items-center gap-2 w-full min-h-[52px] px-2 py-2 m-0 list-none'
                      : 'box-border flex flex-row justify-left items-center gap-1 w-full p-1 m-0 rounded-md border-1 border-white/5 border-solid hover:bg-white/5'
                  }
                  style={{
                    position: 'relative',
                    transformOrigin: 'center'
                  }}
                >
                  <div className='flex flex-row items-center gap-1 w-full'>
                    {editingEnabled && (
                      <motion.button
                        className='btn-h box-border flex justify-center items-center text-white/65 m-0 cursor-grab active:cursor-grabbing'
                        onPointerDown={(e) => e.stopPropagation()}
                        animate={{
                          color: draggedItemIndex === index ? "#0F1D32" : "rgba(255,255,255,0.65)",
                          scale: draggedItemIndex === index ? 1.1 : 1,
                          opacity: draggedItemIndex === index ? 1 : 0.6,
                          backgroundColor: draggedItemIndex === index ? "#2B88F5" : "transparent"
                        }}
                        transition={{
                          duration: 0.2,
                          ease: "easeInOut"
                        }}
                        style={{
                          filter: draggedItemIndex === index ? 'drop-shadow(0 0 6px #2B88F5)' : 'none',
                          borderRadius: '4px'
                        }}
                      >
                        <motion.div
                          animate={{
                            scale: draggedItemIndex === index ? [1, 1.2, 1] : 1,
                          }}
                          transition={{
                            duration: 0.4,
                            ease: "easeInOut",
                            times: [0, 0.25, 0.5, 1]
                          }}
                        >
                          <DotsSixIcon size={20} weight="bold" />
                        </motion.div>
                      </motion.button>
                    )}
                    <div className='flex min-w-0 flex-row items-center justify-between gap-2 p-0 m-0 w-full'>
                      {csamCmType && <CsamDetectionPill type={csamCmType} />}
                      {isStacked ? (
                        <>
                          <div className="flex min-w-0 flex-1 items-center gap-1 pl-0.5 pr-1">
                            <span className="shrink-0">
                              <ChatAttachmentFileIcon
                                filename={stackedSourceFilename(sourceObject, sourceListPathOptions, t)}
                                size={16}
                              />
                            </span>
                            <StackedSourceListText
                              className="min-w-0 flex-1"
                              filename={stackedSourceFilename(sourceObject, sourceListPathOptions, t)}
                              displayPath={formatStackedSourceDisplayPath(sourceListPath)}
                              metadata={formatStackedSourceListAnnotation(sourceObject, t)}
                            />
                          </div>
                          {(isMediaSource(sourceObject, sourceObject.fileInfo, jsonMediaDisplayEnabled) ||
                            isSourceInMedia(sourceObject, mediaValue, newSources)) && (
                            <DisplayMediaToggle
                              isDisplayed={isSourceInMedia(sourceObject, mediaValue, newSources)}
                              label={formatUnifiedSourceListLabel(sourceObject, sourceListPathOptions, t)}
                              onToggle={() => toggleMediaSource(sourceObject)}
                            />
                          )}
                          <div className="flex shrink-0 flex-row items-center gap-2">
                            <Button
                              type="button"
                              iconOnly
                              size="sm"
                              style="text"
                              color="light"
                              background="inverse"
                              className="box-border m-0 shrink-0"
                              aria-label={t('sources.previewNamed', { label: formatUnifiedSourceListLabel(sourceObject, sourceListPathOptions, t) })}
                              aria-haspopup="dialog"
                              onClick={(e) => {
                                e.stopPropagation();
                                openSourcePreview('displayed', index);
                              }}
                            >
                              <MagnifyingGlassPlusIcon size={16} />
                            </Button>
                            {editingEnabled && (
                              <Button
                                iconOnly
                                size="sm"
                                style="text"
                                color="light"
                                background="inverse"
                                className="box-border m-0 shrink-0"
                                aria-label={t('sources.remove')}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeSource(newSources.indexOf(sourceObject));
                                }}
                              >
                                <MinusCircleIcon size={16} />
                              </Button>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                      <UnifiedSourcePathTooltip pathLabel={sourceListPath}>
                      <div className="min-w-0 flex-1 overflow-hidden">
                      <TpPopover
                        isOpen={sourceDetailsPopoverIndex === index}
                        onOpenChange={(open) => {
                          if (!open) setSourceDetailsPopoverIndex(null);
                        }}
                        placement="bottom-start"
                        variant="inverted"
                        maxWidth="31.25rem"
                        showArrow={false}
                        title={path.basename(sourceObject.truncatedFilePath || sourceObject.sourceUrl || 'Unknown')}
                        content={
                          <div className="flex min-w-[280px] max-w-full flex-col gap-2">
                            {(() => {
                              const mt = getMediaType(sourceObject, sourceObject.fileInfo);
                              const context = getUnifiedSourceAnnotationContext(sourceObject);
                              if (mt === 'audio' || mt === 'video') {
                                return (
                                  <>
                                    {!isUnsetMediaTrimTime(context.startTime) && (
                                      <div className="flex flex-row gap-2">
                                        <div className="shrink-0 font-semibold text-[var(--tp-popover-fg)]">{t('sources.start')}</div>
                                        <div className="min-w-0 text-[var(--tp-popover-fg)]">{String(context.startTime)}</div>
                                      </div>
                                    )}
                                    {!isUnsetMediaTrimTime(context.endTime) && (
                                      <div className="flex flex-row gap-2">
                                        <div className="shrink-0 font-semibold text-[var(--tp-popover-fg)]">{t('sources.end')}</div>
                                        <div className="min-w-0 text-[var(--tp-popover-fg)]">{String(context.endTime)}</div>
                                      </div>
                                    )}
                                  </>
                                );
                              }
                              if (isContainerLikeSource(sourceObject, sourceObject.fileInfo)) {
                                return null;
                              }
                              const pageRef =
                                context.pageNumber ?? context.page ?? sourceObject.pageNumber;
                              return pageRef !== undefined && pageRef !== null && pageRef !== '' ? (
                                <div className="flex flex-row gap-2">
                                  <div className="shrink-0 font-semibold text-[var(--tp-popover-fg)]">{t('sources.page')}</div>
                                  <div className="min-w-0 text-[var(--tp-popover-fg)]">{pageRef}</div>
                                </div>
                              ) : null;
                            })()}
                            <div className="flex flex-col gap-2">
                              <div className="font-semibold text-[var(--tp-popover-fg)]">{t('sources.fileLocation')}</div>
                              <div className="flex flex-row items-start gap-2">
                                <div
                                  className={`min-w-0 flex-1 break-all text-sm text-[var(--tp-popover-fg)] max-h-[4.5rem] overflow-y-auto custom-scrollbar2 ${PATH_DISPLAY_WHITESPACE_CLASSNAME}`}
                                >
                                  {extractPathAfterBulk(sourceObject.truncatedFilePath || sourceObject.sourceUrl || 'Unknown', true).truncatedFilePath}
                                </div>
                                <div className="flex shrink-0 flex-row gap-1">
                                  <TpTooltip content={t('sources.copyLocation')} placement="top" delayDuration={0}>
                                    <Button
                                      iconOnly
                                      size="sm"
                                      style="text"
                                      aria-label={t('sources.copyLocation')}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (sourceObject.truncatedFilePath || sourceObject.sourceUrl) {
                                          const pathToCopy =
                                            extractPathAfterBulk(sourceObject.truncatedFilePath || sourceObject.sourceUrl || '', true).truncatedFilePath ||
                                            sourceObject.truncatedFilePath ||
                                            sourceObject.sourceUrl ||
                                            '';
                                          navigator.clipboard.writeText(pathToCopy);
                                        }
                                      }}
                                    >
                                      <FileCloudIcon />
                                    </Button>
                                  </TpTooltip>
                                  {isMediaSource(sourceObject, sourceObject.fileInfo, jsonMediaDisplayEnabled) && mediaSourceShare?.addToMediaShare && (
                                    <TpTooltip content={copiedToMediaShareIndex === index ? t('sources.added') : t('sources.addToFavorites')} placement="top" delayDuration={0}>
                                      <Button
                                        iconOnly
                                        size="sm"
                                        style="text"
                                        color={copiedToMediaShareIndex === index ? "success" : undefined}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          mediaSourceShare.addToMediaShare(sourceObject, () => {
                                            setCopiedToMediaShareIndex(index);
                                            setTimeout(() => setCopiedToMediaShareIndex(null), 1500);
                                          });
                                        }}
                                        aria-label={copiedToMediaShareIndex === index ? t('sources.added') : t('sources.addToFavorites')}
                                      >
                                        {copiedToMediaShareIndex === index ? (
                                          <CheckIcon />
                                        ) : (
                                          <BookmarksSimpleIcon />
                                        )}
                                      </Button>
                                    </TpTooltip>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        }
                      >
                        <button
                          type="button"
                          aria-label={t('sources.viewNamed', { label: formatUnifiedSourceListLabel(sourceObject, sourceListPathOptions, t) })}
                          aria-haspopup="dialog"
                          className={`${UNIFIED_SOURCE_PATH_TRIGGER_CLASSNAME} px-1`}
                          onClick={() => {
                            setSourceDetailsPopoverIndex((current) => (current === index ? null : index));
                          }}
                        >
                          <UnifiedSourceListPathText
                            pathLabel={sourceListPath}
                            modifier={sourceListModifier}
                            sourceTypeAttr={`${getMediaType(sourceObject, sourceObject.fileInfo)}/${sourceObject.sourceType || 'unknown'}`}
                          />
                        </button>
                      </TpPopover>
                      </div>
                      </UnifiedSourcePathTooltip>

                      <div className='flex flex-shrink-0 flex-row items-center gap-2'>
                        <Button
                          iconOnly
                          size="sm"
                          style="text"
                          color="light"
                          background="inverse"
                          className={`box-border m-0 ${isRestrictedFileType(sourceObject) ? 'text-white/20' : ''}`}
                          aria-label={t('sources.download')}
                          onClick={(e) => { e.stopPropagation(); handleDownload(sourceObject) }}
                          disabled={isRestrictedFileType(sourceObject)}
                        >
                          <DownloadSimpleIcon />
                        </Button>

                        {(isMediaSource(sourceObject, sourceObject.fileInfo, jsonMediaDisplayEnabled) ||
                          isSourceInMedia(sourceObject, mediaValue, newSources)) ? (
                          <Button
                            iconOnly
                            size="sm"
                            style="text"
                            color="light"
                            background="inverse"
                            className="box-border m-0"
                            aria-label={isSourceInMedia(sourceObject, mediaValue, newSources) ? t('sources.removeFromViewer') : t('sources.addToViewer')}
                            title={isSourceInMedia(sourceObject, mediaValue, newSources) ? t('sources.removeFromViewer') : t('sources.addToViewer')}
                            onClick={(e) => { e.stopPropagation(); toggleMediaSource(sourceObject) }}
                          >
                            {isSourceInMedia(sourceObject, mediaValue, newSources) ? (
                              <MinusSquareIcon size={20} />
                            ) : (
                              <PlusSquareIcon size={20} />
                            )}
                          </Button>
                        ) : (
                          <div className="w-[28px]" />
                        )}

                        {editingEnabled && (
                          <Button
                            iconOnly
                            size="sm"
                            style="text"
                            color="light"
                            background="inverse"
                            className='box-border m-0'
                            aria-label={t('sources.remove')}
                            onClick={(e) => { e.stopPropagation(); removeSource(newSources.indexOf(sourceObject)) }}
                          >
                            <XIcon />
                          </Button>
                        )}
                      </div>
                        </>
                      )}
                    </div>
                  </div>
                </motion.li>
              </React.Fragment>
            );
          })}
          {dragPosition === newSources?.length && (
            <motion.li
              className="h-[2px] bg-brand-blue my-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
          )}
        </motion.ul>
        </div>
        )}
        {editingEnabled && !(isTitleSlide && ((newSources?.length ?? 0) > 0 || (mediaValue?.length ?? 0) > 0)) && (
          <div
            className={
              isStacked
                ? STACKED_SOURCES_SECTION_SHELL_CLASS
                : 'box-border flex flex-col m-0 mt-[15px] justify-start w-full sm:w-[350px] border-1 border-white/10 border-solid rounded-md p-4 bg-white/5'
            }
          >
            <div
              className={`flex flex-row items-center justify-between ${isStacked ? '' : 'mb-[10px]'}`}
            >
              {isStacked ? (
                <div className="min-w-0 flex-1">
                  <SourcesSectionHeader
                    title={t('sources.available')}
                    count={sourcesForResults?.length ?? 0}
                    tooltip={
                      isTitleSlide
                        ? t('sources.availableTitleTooltip')
                        : t('sources.availableTooltip')
                    }
                    actionLabel={sourcesForResults?.length > 0 ? t('sources.addAll') : undefined}
                    onAction={sourcesForResults?.length > 0 ? handleAddAllSources : undefined}
                  />
                </div>
              ) : (
                <div className="flex flex-row items-center gap-2">
                  <div className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0">{t('sources.add')}</div>
                  <TpTooltip content={t('sources.addTooltip')} placement="top" delayDuration={0}>
                    <InfoIcon size={16} className="text-white/65 cursor-help" />
                  </TpTooltip>
                </div>
              )}
              {sourceFromSessionStorage && (
                <div className="flex flex-row items-center">
                  <TpTooltip
                    content={
                      <div>
                      {t('sources.addCopied')}&nbsp;
                      {((sourceFromSessionStorage as any)?.caseId?.trim() !== caseId) && (
                      <span>
                      {t('sources.addCopiedCase', { caseName: getCaseDisplayName((sourceFromSessionStorage as any)?.caseId?.trim()) })}
                      </span>
                      )}
                      </div>
                    }
                    placement="top"
                    delayDuration={0}
                  >
                    <Button
                      iconOnly
                      size="sm"
                      style="text"
                      color="light"
                      background="inverse"
                      className="box-border m-0"
                      aria-label={t('sources.addCopied')}
                      onClick={addSourceFromSessionStorage}
                    >
                      <ListPlusIcon />
                    </Button>
                  </TpTooltip>
                </div>
              )}
            </div>
            <div className="w-full min-w-0">
              {isStacked ? (
                <AvailableSourcesFilterBar
                  value={inputValue || ''}
                  onChange={setInputValue}
                />
              ) : (
                <div
                  className="flex flex-row items-center justify-between gap-2 rounded-md bg-white p-2 text-black/90 cursor-pointer"
                  onClick={(e) => {
                    const input = e.currentTarget.querySelector('input');
                    input?.focus();
                  }}
                >
                  <input
                    type="text"
                    className="box-border w-full text-[14px] outline-none"
                    aria-label={t('sources.filter')}
                    placeholder={t('sources.searchPlaceholder')}
                    value={inputValue || ''}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                  />
                  <ListMagnifyingGlassIcon size={20} />
                </div>
              )}
              {loadingEvidence && (
                <div className="flex items-center justify-center h-full pb-8">
                  <div className="animate-pulse font-bold">{t('sources.loadingEvidence')}</div>
                </div>
              )}
              {!loadingEvidence && sourcesForResults?.length > 0 ? (
                <div
                  className={
                    isStacked
                      ? 'mt-2 w-full min-w-0 px-2'
                      : 'w-full min-w-0 border border-white/20 rounded-sm mt-3'
                  }
                >
                  <div>
                    {!isStacked && (
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white/65 text-sm">
                          {t('sources.results', { count: sourcesForResults.length })}
                        </span>
                        {sourcesForResults.length > 0 && (
                          <Button
                            style="text"
                            color="accent"
                            background="inverse"
                            size="sm"
                            className="text-brand-blue-light/80 text-sm pr-2 font-semibold"
                            onClick={() => {
                              if (selectedSources.length === sourcesForResults.length) {
                                setSelectedSources([]);
                              } else {
                                setSelectedSources(sourcesForResults.map((_, index) => index));
                              }
                            }}
                          >
                            {selectedSources.length === sourcesForResults.length ? t('sources.deselectAll') : t('sources.selectAll')}
                          </Button>
                        )}
                      </div>
                    )}
                    <div className={`max-h-[260px] overflow-y-auto custom-scrollbar2 flex flex-col min-w-0 ${isStacked ? 'gap-0' : 'gap-1 pr-2'}`}>
                      {sourcesForResults.map((sourceObject, index) => {
                        const addSourceListModifier = formatUnifiedSourceListAnnotation(sourceObject, t);
                        const sourceListPath = formatUnifiedSourceListPath(sourceObject, sourceListPathOptions, t);
                        const isAddSourcePreviewOpen = previewPopoverOpen === index;
                        const csamCmType = canSeeCsamTags
                          ? sourceContentModerationType(sourceObject)
                          : undefined;

                        return (
                        <div
                          key={`${sourceObject.sourceUrl}-${index}`}
                          className={
                            isStacked
                              ? 'box-border flex h-[52px] w-full min-w-0 flex-row items-center gap-2 rounded px-2 py-2'
                              : 'box-border px-2 py-1 flex flex-row justify-left items-center gap-1 rounded-md border-1 border-white/5 border-solid hover:bg-white/5 cursor-pointer min-w-0 w-full'
                          }
                        >
                          {csamCmType && <CsamDetectionPill type={csamCmType} />}
                          {isStacked ? (
                            <>
                              <div className="flex min-w-0 flex-1 items-center gap-1 pl-0.5 pr-1">
                                <span className="shrink-0">
                                  <ChatAttachmentFileIcon
                                    filename={stackedSourceFilename(sourceObject, sourceListPathOptions, t)}
                                    size={16}
                                  />
                                </span>
                                <StackedSourceListText
                                  className="min-w-0 flex-1"
                                  filename={stackedSourceFilename(sourceObject, sourceListPathOptions, t)}
                                  displayPath={formatStackedSourceDisplayPath(sourceListPath)}
                                  metadata={formatStackedSourceListAnnotation(sourceObject, t)}
                                />
                              </div>
                              <div className="flex shrink-0 flex-row items-center gap-2">
                              <TpTooltip
                                placement="right"
                                delayDuration={100}
                                sideOffset={4}
                                content={formatProvenanceStatusLabel((sourceObject as any).status)}
                              >
                                <Button
                                  type="button"
                                  iconOnly
                                  size="sm"
                                  style="text"
                                  color="light"
                                  background="inverse"
                                  className="box-border shrink-0 m-0"
                                  aria-label={formatProvenanceStatusLabel((sourceObject as any).status)}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                >
                                  <ProvenanceIcon
                                    status={(sourceObject as any).status}
                                    size={getProvenanceIconDisplaySize((sourceObject as any).status)}
                                    className={getProvenanceToolbarIconClassNames(
                                      (sourceObject as any).status,
                                      { withBorder: false },
                                    )}
                                    aria-hidden
                                  />
                                </Button>
                              </TpTooltip>
                              <Button
                                type="button"
                                iconOnly
                                size="sm"
                                style="text"
                                color="light"
                                background="inverse"
                                className="box-border shrink-0 m-0"
                                aria-label={t('sources.previewNamed', { label: formatUnifiedSourceListLabel(sourceObject, sourceListPathOptions, t) })}
                                aria-haspopup="dialog"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openSourcePreview('available', index);
                                }}
                              >
                                <MagnifyingGlassPlusIcon size={16} />
                              </Button>
                              <TpTooltip content={t('sources.addThis')} placement="top" delayDuration={0}>
                                <Button
                                  type="button"
                                  iconOnly
                                  size="sm"
                                  style="text"
                                  color="light"
                                  background="inverse"
                                  className="box-border shrink-0 m-0"
                                  aria-label={t('sources.addNamed', { label: formatUnifiedSourceListLabel(sourceObject, sourceListPathOptions, t) })}
                                  data-testid="available-source-add"
                                  disabled={isTitleSlide && newSources.length > 0}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPreviewPopoverOpen(-1);
                                    handleAddSources([sourceObject]);
                                  }}
                                >
                                  <PlusCircleIcon size={16} />
                                </Button>
                              </TpTooltip>
                              </div>
                            </>
                          ) : (
                          <>
                          <UnifiedSourcePathTooltip
                            pathLabel={sourceListPath}
                            disabled={isAddSourcePreviewOpen}
                          >
                          {isInModal ? (
                            <div className="min-w-0 flex-1 overflow-hidden">
                            <button
                              type="button"
                              aria-label={t('sources.previewNamed', { label: formatUnifiedSourceListLabel(sourceObject, sourceListPathOptions, t) })}
                              aria-haspopup="dialog"
                              className={UNIFIED_SOURCE_PATH_TRIGGER_CLASSNAME}
                              onClick={(e) => {
                                e.stopPropagation();
                                openSourcePreview('available', index);
                              }}
                            >
                              <UnifiedSourceListPathText
                                pathLabel={sourceListPath}
                                modifier={addSourceListModifier}
                              />
                            </button>
                            </div>
                          ) : (
                          <div className="min-w-0 flex-1 overflow-hidden">
                          <Popover
                            placement='right'
                            showArrow={true}
                            shouldFlip={true}
                            offset={40}
                            crossOffset={2}
                            autoFocus={true}
                            className="outline-none"
                            classNames={{
                              base: 'outline-none before:bg-gray-700 before:rounded-[2px] before:shadow-none before:drop-shadow-none border-0 before:w-[25px] before:h-[25px]',
                            }}
                            isOpen={previewPopoverOpen === index}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                e.stopPropagation();
                                setPreviewPopoverOpen(-1);
                              }
                            }}
                          >
                            <PopoverTrigger>
                              <button
                                type="button"
                                aria-label={t('sources.previewNamed', { label: formatUnifiedSourceListLabel(sourceObject, sourceListPathOptions, t) })}
                                aria-haspopup="dialog"
                                className={UNIFIED_SOURCE_PATH_TRIGGER_CLASSNAME}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openSourcePreview('available', index);
                                }}
                              >
                                <UnifiedSourceListPathText
                                  pathLabel={sourceListPath}
                                  modifier={addSourceListModifier}
                                />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className={`max-w-[450px] ${MEDIA_PREVIEW_OVERLAY_SURFACE_CLASS} rounded-md overflow-y-auto custom-scrollbar2`}>
                              {renderAddSourcePreviewPanel('popover')}
                            </PopoverContent>
                          </Popover>
                          </div>
                          )}
                          </UnifiedSourcePathTooltip>
                          <TpTooltip
                            placement="right"
                            delayDuration={100}
                            sideOffset={4}
                            content={formatProvenanceStatusLabel((sourceObject as any).status)}
                          >
                            <Button
                              type="button"
                              iconOnly
                              size="sm"
                              style="text"
                              color="light"
                              background="inverse"
                              className="box-border shrink-0 m-0"
                              aria-label={formatProvenanceStatusLabel((sourceObject as any).status)}
                              key={`status-${(sourceObject as any).url}-${(sourceObject as any).status}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <ProvenanceIcon
                                status={(sourceObject as any).status}
                                size={getProvenanceIconDisplaySize((sourceObject as any).status)}
                                className={getProvenanceToolbarIconClassNames(
                                  (sourceObject as any).status,
                                  { withBorder: false },
                                )}
                                aria-hidden
                              />
                            </Button>
                          </TpTooltip>
                          <input
                            type="checkbox"
                            checked={selectedSources.includes(index)}
                            onChange={(e) => {
                              e.stopPropagation();
                              setPreviewPopoverOpen(-1);
                              const newSelected = e.target.checked
                                ? isTitleSlide ? [index] : [...selectedSources, index]
                                : isTitleSlide ? [] : selectedSources.filter(i => i !== index);
                              setSelectedSources(newSelected);
                              if (e.target.checked && addButtonRef.current) {
                                setTimeout(() => {
                                  addButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                }, 100);
                              }
                            }}
                            className="w-4 h-4 shrink-0 rounded border-white/20 text-brand-blue focus:ring-brand-blue cursor-pointer"
                          />
                          </>
                          )}
                        </div>
                        );
                      })}
                    </div>
                    {!isStacked && (
                      <div ref={addButtonRef} className="mt-4 flex justify-end">
                        {selectedSources?.length > 0 && (
                          <Button
                            style="solid"
                            color="accent"
                            background="inverse"
                            className="px-4 py-2"
                            onClick={() => handleAddSources()}
                            disabled={isTitleSlide && newSources.length > 0}
                          >
                            {t('sources.addSelected', { count: selectedSources.length })}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {!loadingEvidence && searchTerm && searchTerm.length > 2 ? (
                    isStacked ? (
                      <div className="mt-2">
                        <SourcesEmptyState>{t('sources.noFilter')}</SourcesEmptyState>
                      </div>
                    ) : (
                      <div className="text-white/65 text-sm text-center py-4">
                        {t('sources.noSearch')}
                      </div>
                    )
                  ) : (
                    null
                  )}
                </>
              )}
            </div>
          </div>
        )}
        {editingEnabled && isTitleSlide && ((newSources?.length ?? 0) > 0 || (mediaValue?.length ?? 0) > 0) && (
          <div
            className={
              isStacked
                ? STACKED_SOURCES_SECTION_SHELL_CLASS
                : 'box-border flex flex-col m-0 mt-[15px] justify-start w-full sm:w-[350px] border-1 border-white/10 border-solid rounded-md p-4 bg-white/5'
            }
          >
            <div className="flex flex-col items-center justify-center gap-4 py-8 px-4">
              <div className="text-white/80 text-sm text-center">
                {t('sources.titleOne')}
              </div>
              <Button
                style="solid"
                color="secondary"
                background="default"
                className="px-4 py-2"
                onClick={handleRemoveAllSources}
              >
                {t('sources.removeCurrent')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Displayed Media — stacked layout leads with the thumbnail strip and has no inline player. */}
      {isStacked && (
        <div className={`${STACKED_SOURCES_SECTION_SHELL_CLASS} order-1`}>
          <SourcesSectionHeader
            title={t('sources.displayedMedia')}
            count={mediaValue?.length ?? 0}
            tooltip={
              isTitleSlide
                ? t('sources.displayedMediaTitleTooltip')
                : t('sources.displayedMediaTooltip')
            }
            actionLabel={mediaValue?.length > 0 ? t('sources.clearAll') : undefined}
            onAction={mediaValue?.length > 0 ? handleClearAllMedia : undefined}
          />
          {mediaValue?.length > 0 ? (
            <>
              {eventMediaPdfCaptureHost}
              <DisplayedMediaStrip
                media={mediaValue.map(resolveMediaForDisplay)}
                selectedIndex={currentMediaIndex}
                editingEnabled={editingEnabled}
                caseId={caseId}
                jsonMediaDisplayEnabled={jsonMediaDisplayEnabled}
                isFullscreen={isFullscreen}
                onSelect={setCurrentMediaIndex}
                onEdit={openThumbnailEditor}
                onRemove={removeMediaAtIndex}
              />
            </>
          ) : (
            <div className="px-2">
              <SourcesEmptyState variant="tile">
                {isTitleSlide
                  ? t('sources.pickAvailable')
                  : t('sources.pickDisplayed')}
              </SourcesEmptyState>
            </div>
          )}
        </div>
      )}

      {/* Media Display - Right Side */}
      {!isStacked && newSources?.length > 0 && (
        <div
          // Plain pixel max-width (no `min(100%, …)` / `max-w-1/2`) — this column sits
          // inside an ancestor sized via `width: fit-content` (EventModalSourcesSection
          // in eventModal.tsx). A percentage inside min()/max() is indefinite while that
          // ancestor computes its own intrinsic/max-content width, so browsers resolve
          // it as 0 for that pass; the ancestor then sizes itself as if this column
          // needed no width at all, and the column's *final* resolved width — even
          // though this element's own definite-pixel children (eventModalMediaBox's
          // box below) still want the room — ends up clamped to whatever the
          // under-sized ancestor happened to compute. That silently collapsed this
          // column under the 600px side-by-side threshold in player.tsx, so "beside"
          // always rendered as "below" no matter what the Controls toggle said (Add
          // New Event follow-up, TR-1589). Same pitfall for below-transcript: a
          // percentage half-width left dead space beside the player under the wider
          // thumbnail strip and skewed UnifiedSources off-center in the modal.
          className="box-border flex flex-col gap-3 min-w-0 w-full max-w-full sm:flex-1 transition-[max-width,min-width] duration-200"
          style={
            isInModal
              ? { maxWidth: `min(100%, ${eventModalOuterMaxWidthPx}px)` }
              : { maxWidth: `${eventModalOuterMaxWidthPx}px` }
          }
        >
          <div className="flex items-center gap-2 mb-[1px]">
            <label className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0">{t('sources.selectedMedia')}</label>
            <TpTooltip content={t('sources.selectedMediaTooltip')} placement="top" delayDuration={0}>
              <InfoIcon size={16} className="text-white/65 cursor-help" />
            </TpTooltip>
          </div>
          {mediaValue?.length < 1 ? (
            <div className="text-white/65 text-sm text-center py-4">
              {t('sources.clickPlus')}
            </div>
          ) : (
            <div className="box-border flex flex-col justify-start items-stretch gap-3">
              <div
                className="box-border flex flex-row justify-start items-start gap-2 p-0 m-0 max-w-full w-full overflow-x-auto custom-scrollbar2"
              >
                {mediaValue.map((media, index) => {
                  const displayMedia = resolveMediaForDisplay(media);
                  return (
                  <div
                    key={`${displayMedia.mediaUrl || displayMedia.sourceUrl || index}-${index}`}
                    className="box-border relative flex flex-col items-start justify-start p-1 rounded-sm cursor-pointer"
                    onClick={() => setCurrentMediaIndex(index)}
                  >
                    <Button
                      iconOnly
                      size="sm"
                      style="text"
                      color="light"
                      background="inverse"
                      className={`absolute top-0 right-0 z-[1001] ${
                        currentMediaIndex === index ? 'opacity-100' : 'opacity-0'
                      }`}
                      aria-label={t('sources.removeMedia')}
                      onClick={(e) => {
                        e.stopPropagation();
                        const updatedMedia = mediaValue.filter((_, i) => i !== index);
                        handleUnifiedSourcesChanges?.(newSources, updatedMedia);
                        if (currentMediaIndex >= updatedMedia.length) {
                          setCurrentMediaIndex(Math.max(0, updatedMedia.length - 1));
                        }
                      }}
                    >
                      <XIcon />
                    </Button>
                    <div className={`z-[1000] box-border flex flex-row items-center justify-center w-[122px] overflow-hidden
                        rounded m-0 p-0 h-[110px] min-h-[110px] bg-white/5 border-1 border-solid
                        ${currentMediaIndex === index ? 'opacity-100 border-white/50' : 'opacity-30 hover:border-white/10'}
                      `}
                    >
                      {(!displayMedia.mediaSrc && !displayMedia.mediaUrl && !(displayMedia.mediaType === 'json' && displayMedia.content?.trim())) && (
                        <div className='p-2 flex justify-center items-center h-full'>
                          <span className='text-center line-clamp-3 overflow-hidden text-ellipsis'>{t('sources.noMedia')}</span>
                        </div>
                      )}
                      {!isFullscreen && displayMedia.mediaType && isDisplayableMediaType(displayMedia.mediaType, { jsonMediaDisplay: jsonMediaDisplayEnabled }) && (displayMedia.mediaSrc || displayMedia.mediaUrl || (displayMedia.mediaType === 'json' && !!displayMedia.content?.trim())) && (
                        <div className="flex flex-col items-center justify-center w-full h-full">
                          <MediaDisplay
                            mediaItem={displayMedia}
                            isUnifiedSources={true}
                            containerHeight={100}
                            containerWidth={120}
                            caseId={caseId}
                          />
                        </div>
                      )}
                      {displayMedia.mediaType === 'youtube' && (displayMedia.mediaSrc || displayMedia.mediaUrl) && (
                        <img
                          src={`https://img.youtube.com/vi/${displayMedia.mediaSrc?.split('/').pop() || displayMedia.mediaUrl?.split('/').pop()}/0.jpg`}
                          alt={t('sources.youtubeThumb')}
                          className='w-full h-full object-cover rounded cursor-pointer'
                        />
                      )}
                    </div>
                    <div className="text-white/65 text-[11px] mt-1 text-center w-full">
                      {(() => {
                        const filename = mediaCaptionPath(displayMedia).split('/').pop() || '';
                        const isAv = displayMedia.mediaType === 'audio' || displayMedia.mediaType === 'video';
                        const rawStart = displayMedia.mediaContext?.startTime;
                        const rawEnd = displayMedia.mediaContext?.endTime;
                        // Match Selected Sources: hide unset / full-file zeros
                        // including formatted clock strings (``0:00`` / ``0:00:00``).
                        const startTimeInfo =
                          isAv && !isUnsetMediaTrimTime(rawStart)
                            ? ` ${t('sources.startParen', { time: formatTimeIndex(String(rawStart)) })}`
                            : '';
                        const endTimeInfo =
                          isAv && !isUnsetMediaTrimTime(rawEnd)
                            ? ` ${t('sources.endParen', { time: formatTimeIndex(String(rawEnd)) })}`
                            : '';
                        const timeInfo = `${startTimeInfo}${endTimeInfo}`;
                        const truncatedFilename =
                          filename.length > (timeInfo ? 9 : 15)
                            ? '...' + filename.slice(-(timeInfo ? 9 : 15))
                            : filename;
                        return truncatedFilename + timeInfo;
                      })()}
                    </div>
                  </div>
                  );
                })}
              </div>
              {mediaValue && mediaValue?.length > 0 && (
                <div
                  ref={eventModalPlayerRowRef}
                  className="flex flex-row items-center w-full min-w-0 max-w-full box-border p-1 border-1 border-white/20 border-solid rounded-sm overflow-hidden"
                >
                  {!isFullscreen && mediaValue?.length > 1 && (
                    <Button
                      iconOnly
                      size="sm"
                      style="text"
                      color="light"
                      background="inverse"
                      className="justify-start disabled:opacity-20 shrink-0"
                      aria-label={t('sources.previousMedia')}
                      onClick={() => setCurrentMediaIndex(currentMediaIndex - 1)}
                      disabled={currentMediaIndex === 0}
                    >
                      <CaretLeftIcon />
                    </Button>
                  )}
                  {mediaValue?.length > 0 && mediaValue[currentMediaIndex] && (
                    <div className="min-w-0 flex-1 max-w-full overflow-hidden">
                    <MediaDisplay
                      mediaItem={resolveMediaForDisplay(mediaValue[currentMediaIndex])}
                      mediaSelectionKey={String(currentMediaIndex)}
                      isEventModal={true}
                      updateMediaContext={updateMediaContext}
                      editingEnabled={editingEnabled}
                      isTitleSlide={isTitleSlide}
                      containerHeight={eventModalEffectiveMediaBox.height}
                      containerWidth={eventModalEffectiveMediaBox.width}
                      caseId={caseId}
                    />
                    </div>
                  )}
                  {mediaValue?.length > 1 && (
                    <Button
                      iconOnly
                      size="sm"
                      style="text"
                      color="light"
                      background="inverse"
                      className="justify-end disabled:opacity-20 shrink-0"
                      aria-label={t('sources.nextMedia')}
                      onClick={() => setCurrentMediaIndex(currentMediaIndex + 1)}
                      disabled={currentMediaIndex === mediaValue.length - 1}
                    >
                      <CaretRightIcon />
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {isInModal &&
        previewPopoverOpen >= 0 &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <DismissibleBlurOverlay
              position="fixed"
              zIndexClass="z-[100001]"
              className="bg-black/50"
              onClose={closeAddSourcePreview}
              closeLabel={t('sources.closePreviewOverlay')}
              dismissOnBackdropClick
              showCloseButton={false}
            />
            <div className="fixed inset-0 z-[100002] flex items-center justify-center p-3 sm:p-4 pointer-events-none">
              <div
                ref={addSourcePreviewDialogRef}
                role="dialog"
                aria-modal="true"
                aria-label={t('sources.preview')}
                tabIndex={-1}
                className={`pointer-events-auto flex flex-col outline-none ${EVIDENCE_LOCKER_PREVIEW_DIALOG_CLASS}`}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    closeAddSourcePreview();
                  }
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="custom-scrollbar2 flex min-h-0 flex-1 flex-col overflow-hidden">
                  {renderAddSourcePreviewPanel('dialog')}
                </div>
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};

UnifiedSources.displayName = 'UnifiedSources';

export default UnifiedSources;
